import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  DatePicker,
  Button,
  Typography,
  Table,
  Statistic,
  Row,
  Col,
  Spin,
  Empty,
  message,
  Result,
  Modal,
  Tag,
  Tooltip,
  Pagination,
  Select,
  Form,
  Input,
  InputNumber,
  Radio,
  Popconfirm
} from 'antd'
import {
  CalendarOutlined,
  ReloadOutlined,
  DollarOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined as CalendarIcon,
  WalletOutlined,
  CalculatorOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { now as tzNow } from '../utils/timezone'

const { Title, Paragraph, Text } = Typography

// Premium color palette
const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  accent: '#8B9DC3',
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
}

const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px',
  xxl: '64px'
}

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`

const StyledCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .ant-card-body {
    padding: ${spacing.lg};
  }

  @media (max-width: 820px) {
    .ant-card-body {
      padding: ${spacing.md};
    }
  }
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  align-items: center;
`

const TableCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .ant-card-head {
    border-bottom: 1px solid ${colors.border};
    padding: ${spacing.md} ${spacing.lg};

    .ant-card-head-title {
      font-size: 18px;
      font-weight: 500;
      color: ${colors.text.primary};
    }
  }

  .ant-card-body {
    padding: ${spacing.lg};
  }

  @media (max-width: 820px) {
    .ant-card-head {
      padding: ${spacing.md};
    }
    .ant-card-body {
      padding: ${spacing.md};
    }
  }
`

const AmountText = styled.span`
  font-weight: 500;
  color: ${(props) => (props.negative ? colors.error : colors.text.primary)};
`

const PerformanceText = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-top: 2px;
`

const TierDisplayWrap = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.border};
`

const TierDisplayMain = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  letter-spacing: 0.02em;
`

const TierDisplaySub = styled.span`
  font-size: 13px;
  color: ${colors.text.secondary};
  font-weight: 400;
`

const TierDisplayGuarantee = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 8px;
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.border};
  font-size: 14px;
  color: ${colors.text.secondary};
  font-weight: 500;
`

const DetailPageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 0;
  background: ${colors.background.secondary};
  position: relative;
  overflow-x: hidden;
`

const DetailHeader = styled.div`
  background: ${colors.background.primary};
  padding: ${spacing.xl} ${spacing.lg};
  border-bottom: 1px solid ${colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  @media (max-width: 820px) {
    padding: ${spacing.sm} ${spacing.sm};

    .detail-title {
      font-size: 18px !important;
      margin-bottom: ${spacing.xs} !important;
    }

    .detail-meta {
      flex-direction: column;
      align-items: flex-start !important;
      gap: ${spacing.xs} !important;
      font-size: 12px !important;
    }
  }
`

const BackButton = styled(Button)`
  margin-bottom: ${spacing.md};
  border: 1px solid ${colors.border};
  color: ${colors.text.secondary};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.secondary};
    color: ${colors.secondary};
  }

  @media (max-width: 820px) {
    margin-bottom: ${spacing.sm};
    padding: 4px 8px;
    height: auto;
    font-size: 12px;
  }
`

const DetailContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.lg};
  position: relative;

  @media (max-width: 820px) {
    padding: ${spacing.sm};
  }
`

const SummaryCard = styled.div`
  background: ${colors.background.primary};
  border-radius: 12px;
  padding: ${spacing.lg};
  margin-top: ${spacing.lg};
  border: 1px solid ${colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};

  @media (max-width: 820px) {
    padding: ${spacing.sm};
    grid-template-columns: repeat(2, 1fr);
    gap: ${spacing.xs};
    margin-top: ${spacing.md};
  }
`

const SummaryItem = styled.div`
  text-align: center;
  padding: ${spacing.md};
  border-radius: 8px;
  background: ${(props) =>
    props.highlight ? colors.background.tertiary : colors.background.secondary};
  border: 1px solid
    ${(props) => (props.highlight ? colors.secondary : colors.border)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.secondary};
  }

  @media (max-width: 820px) {
    padding: ${spacing.sm};
    border-radius: 6px;
  }
`

const SummaryLabel = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin-bottom: ${spacing.xs};
  font-weight: 400;

  @media (max-width: 820px) {
    font-size: 11px;
    margin-bottom: 4px;
  }
`

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  color: ${colors.text.primary};

  @media (max-width: 820px) {
    font-size: 16px;
    line-height: 1.3;
  }
`

const SectionCard = styled.div`
  background: ${colors.background.primary};
  border-radius: 12px;
  padding: ${spacing.lg};
  margin-top: ${spacing.lg};
  border: 1px solid ${colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  @media (max-width: 820px) {
    padding: ${spacing.sm};
    margin-top: ${spacing.sm};
    border-radius: 8px;
  }
`

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: ${spacing.md};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${colors.border};

  @media (max-width: 820px) {
    font-size: 14px;
    margin-bottom: ${spacing.sm};
    padding-bottom: ${spacing.xs};
  }
`

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ItemCard = styled.div`
  background: ${colors.background.primary};
  border-radius: 6px;
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border};
  border-left: 3px solid
    ${(props) => (props.type === 'addition' ? colors.success : colors.error)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      props.type === 'addition' ? colors.success : colors.error};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
`

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.sm};
  gap: ${spacing.sm};
`

const ItemInfo = styled.div`
  flex: 1;
`

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px ${spacing.sm};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: ${spacing.sm};
  background: ${(props) =>
    props.type === 'addition' ? `${colors.success}15` : `${colors.error}15`};
  color: ${(props) =>
    props.type === 'addition' ? colors.success : colors.error};
  border: 1px solid
    ${(props) =>
      props.type === 'addition' ? `${colors.success}30` : `${colors.error}30`};
`

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: ${spacing.xs};
  line-height: 1.4;
`

const ItemMeta = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  margin-top: ${spacing.xs};
`

const ItemAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.negative ? colors.error : colors.success)};
  white-space: nowrap;
`

const ItemDetails = styled.div`
  margin-top: ${spacing.sm};
  padding-top: ${spacing.sm};
  border-top: 1px solid ${colors.border};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${spacing.xs} ${spacing.sm};
  font-size: 12px;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  color: ${colors.text.secondary};
`

const DetailLabel = styled.span`
  font-weight: 500;
  color: ${colors.text.tertiary};
`

const TotalSection = styled.div`
  margin-top: ${spacing.md};
  padding-top: ${spacing.md};
  border-top: 1px solid ${colors.border};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.md};

  @media (max-width: 820px) {
    margin-top: ${spacing.sm};
    padding-top: ${spacing.sm};
    gap: ${spacing.sm};
  }
`

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text.primary};

  @media (max-width: 820px) {
    font-size: 13px;
  }
`

const TotalValue = styled.span`
  font-size: 24px;
  font-weight: 500;
  color: ${(props) => (props.negative ? colors.error : colors.success)};

  @media (max-width: 820px) {
    font-size: 18px;
  }
`

const StyledTable = styled(Table)`
  .ant-table-tbody > tr {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: ${colors.background.tertiary};
    }
  }

  .ant-table-tbody > tr > td {
    padding: ${spacing.sm} ${spacing.md};
  }

  @media (max-width: 820px) {
    .ant-table-thead > tr > th {
      padding: ${spacing.xs} ${spacing.sm};
      font-size: 12px;
      white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
      padding: ${spacing.xs} ${spacing.sm};
      font-size: 12px;
      white-space: nowrap;
    }

    .ant-table {
      font-size: 12px;
    }

    /* 確保固定列在手機版可見 */
    .ant-table-cell-fix-left {
      z-index: 1 !important;
      background: white !important;
    }

    /* 員工名稱列在手機版確保可見且固定 */
    .ant-table-cell[data-column-key='employeeName'],
    .ant-table-thead .ant-table-cell[data-column-key='employeeName'] {
      position: sticky !important;
      left: 0 !important;
      background: white !important;
      z-index: 2 !important;
      min-width: 100px !important;
      max-width: 120px !important;
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    }
  }
`

const StyledModal = styled(Modal)`
  .modal-title-content {
    font-size: 16px;
  }

  @media (max-width: 820px) {
    .ant-modal {
      max-width: calc(100vw - 16px) !important;
      margin: 8px auto !important;
    }

    .ant-modal-content {
      padding: ${spacing.sm} !important;
    }

    .ant-modal-header {
      padding: ${spacing.sm} !important;
    }

    .modal-title-content {
      font-size: 14px;
    }

    .ant-modal-body {
      padding: ${spacing.sm} !important;
      max-height: calc(100vh - 120px);
      overflow-y: auto;
    }

    .ant-modal-footer {
      padding: ${spacing.sm} !important;
    }
  }
`

const DetailModalContent = styled.div`
  .detail-section {
    margin-bottom: ${spacing.lg};
  }

  .detail-section-title {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.text.primary};
    margin-bottom: ${spacing.md};
    padding-bottom: ${spacing.sm};
    border-bottom: 1px solid ${colors.border};
  }

  @media (max-width: 820px) {
    .detail-section {
      margin-bottom: ${spacing.md};
    }

    .detail-section-title {
      font-size: 14px;
      margin-bottom: ${spacing.sm};
      padding-bottom: ${spacing.xs};
    }
  }
`

const TeamBonusNotice = styled.div`
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #613400;
`

const ItemDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.sm};
  font-size: 13px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: ${spacing.xs};
    font-size: 12px;
  }
`

// 手機版薪資明細卡片
const MobileSalaryCard = styled.div`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: ${spacing.sm};
  margin-bottom: ${spacing.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.sm};

  &:active {
    background: ${colors.background.secondary};
    border-color: ${colors.secondary};
    transform: scale(0.98);
  }

  &:hover {
    border-color: ${colors.secondary};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`

const MobileCardLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MobileCardEmployeeName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobileCardSalary = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  line-height: 1.2;
`

const MobileCardIcon = styled.div`
  color: ${colors.text.tertiary};
  font-size: 16px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.xs} 0;
`

const formatCurrency = (value) => {
  const numeric = Number(value) || 0
  return `NT$ ${numeric.toLocaleString()}`
}

const normalizeSalaryItems = (payload) => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.employees)) return payload.employees
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.items)) return payload.items
  return []
}

const MonthlySalarySummaryPage = () => {
  const { user, permissions, businessRole, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [selectedMonth, setSelectedMonth] = useState(tzNow())
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [lastUpdated, setLastUpdated] = useState(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [itemDetailModalVisible, setItemDetailModalVisible] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [additionsCategoryFilter, setAdditionsCategoryFilter] = useState('')
  // 加項明細「項目名稱」篩選（可多選，例：只看光學美胸保養課程）
  const [additionsItemFilter, setAdditionsItemFilter] = useState([])
  const [deductionsCategoryFilter, setDeductionsCategoryFilter] = useState('')
  const [exportCsvLoading, setExportCsvLoading] = useState(false)
  // 手動加項／減項
  const [adjustments, setAdjustments] = useState([])
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false)
  const [adjustModalVisible, setAdjustModalVisible] = useState(false)
  const [adjustSubmitting, setAdjustSubmitting] = useState(false)
  const [adjustForm] = Form.useForm()
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820)
  const [mobilePage, setMobilePage] = useState(1)
  const [mobilePageSize] = useState(10) // 手機版每頁顯示10筆

  const businessId = user?.businessId
  const permissionList = Array.isArray(permissions) ? permissions : []
  const isOwner = businessRole === 'owner' || permissionList.includes('*')
  const currentUserId =
    user?.userId || user?._id || user?.id || user?.employeeId || null
  const userNameCandidates = useMemo(() => {
    const candidates = [
      user?.name,
      user?.username,
      user?.displayName,
      user?.profile?.name
    ]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase())
    return Array.from(new Set(candidates))
  }, [user])

  // 穩定的月份字符串，避免無限循環
  const monthString = useMemo(
    () => selectedMonth.format('YYYY-MM'),
    [selectedMonth.valueOf()]
  )

  // 當月份改變時，重置分頁
  useEffect(() => {
    setMobilePage(1)
  }, [monthString])

  const fetchMonthlySalary = useCallback(
    async (monthValue, recalculate = false) => {
      if (!businessId || !monthValue) return
      try {
        setLoading(true)
        const response = await api.get(
          `/api/businesses/${businessId}/commission/monthly-salary`,
          {
            params: {
              month: monthValue.format('YYYY-MM'),
              recalculate: recalculate ? 'true' : 'false'
            }
          }
        )

        const payload =
          response.data?.data ||
          response.data?.salary ||
          response.data?.records ||
          response.data ||
          []
        const list = normalizeSalaryItems(payload)

        setRecords(
          list.map((item, index) => {
            const employeeId =
              item.employeeId ||
              item.employee?.id ||
              item.employee?._id ||
              item._id ||
              null

            return {
              key: employeeId || index,
              employeeId,
              employeeName:
                item.employeeName ||
                item.name ||
                item.employee?.name ||
                '未命名員工',
              // 新增欄位（向後相容）
              month: item.month || monthString,
              guaranteedSalary:
                Number(item.guaranteedSalary ?? item.salary ?? 0) || 0,
              totalSales: Number(item.totalSales ?? 0) || 0,
              tier: item.tier ?? 0,
              tierPercentage: Number(item.tierPercentage ?? 0) || 0,
              nextTierThreshold:
                item.nextTierThreshold != null
                  ? Number(item.nextTierThreshold)
                  : undefined,
              nextTierPercentage:
                item.nextTierPercentage != null
                  ? Number(item.nextTierPercentage)
                  : undefined,
              serviceCommission: Number(item.serviceCommission ?? 0) || 0,
              valueAddedCommission: Number(item.valueAddedCommission ?? 0) || 0,
              productSalesCommission:
                Number(item.productSalesCommission ?? 0) || 0,
              classTicketCommission:
                Number(item.classTicketCommission ?? 0) || 0,
              classTicketPerformance:
                Number(item.classTicketPerformance ?? 0) || 0,
              totalCommission: Number(item.totalCommission ?? 0) || 0,
              performanceBreakdown: item.performanceBreakdown || null,
              servicePerformance: Number(item.servicePerformance ?? 0) || 0,
              valueAddedPerformance:
                Number(item.valueAddedPerformance ?? 0) || 0,
              productPerformance: Number(item.productPerformance ?? 0) || 0,
              baseSalary:
                Number(
                  item.baseSalary ?? item.guaranteedSalary ?? item.salary ?? 0
                ) || 0,
              usedGuarantee: item.usedGuarantee ?? false,
              // 薪資制度：guaranteed_or_commission（保底制）/ base_plus_commission（底薪＋抽成）
              salaryMode: item.salaryMode || 'guaranteed_or_commission',
              // 原有欄位
              additionsTotal:
                Number(
                  item.additionsTotal ??
                    item.totalAdditions ??
                    item.additions ??
                    0
                ) || 0,
              deductionsTotal:
                Number(
                  item.deductionsTotal ??
                    item.totalDeductions ??
                    item.deductions ??
                    0
                ) || 0,
              netSalary:
                Number(
                  item.netSalary ??
                    item.actualSalary ??
                    item.amount ??
                    item.total ??
                    0
                ) || 0,
              // 員工自收項目（客人直接付給員工、員工繳回店家）：不計入薪資
              selfCollected: item.selfCollected || null
            }
          })
        )
        setLastUpdated(tzNow().toDate())
      } catch (error) {
        console.error('取得每月員工薪資失敗', error)
      } finally {
        setLoading(false)
      }
    },
    [businessId]
  )

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchMonthlySalary(selectedMonth)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, monthString, hasFeature])

  // 所有 Hooks 必須在權限檢查之前
  const fetchSalaryDetails = useCallback(
    async (employeeId, monthValue) => {
      if (!businessId || !employeeId || !monthValue) return
      try {
        setDetailLoading(true)
        const response = await api.get(
          `/api/businesses/${businessId}/commission/monthly-salary/details`,
          {
            params: {
              employeeId,
              month: monthValue.format('YYYY-MM')
            }
          }
        )

        const payload =
          response.data?.data || response.data?.details || response.data || null
        const valueAddedDetail =
          response.data?.valueAddedDetail ?? payload?.valueAddedDetail

        // 合併 API 返回的詳細數據，保留已有的基本字段（如果 API 沒有返回）
        setDetailData((prev) => {
          if (!prev)
            return {
              ...payload,
              valueAddedDetail: valueAddedDetail ?? prev?.valueAddedDetail
            }
          return {
            ...prev,
            ...payload,
            valueAddedDetail:
              valueAddedDetail ??
              payload?.valueAddedDetail ??
              prev?.valueAddedDetail,
            // 如果 API 返回了這些字段，使用 API 的值；否則保留之前的值
            guaranteedSalary:
              payload?.guaranteedSalary ?? prev.guaranteedSalary,
            totalSales: payload?.totalSales ?? prev.totalSales,
            tier: payload?.tier ?? prev.tier,
            tierPercentage: payload?.tierPercentage ?? prev.tierPercentage,
            nextTierThreshold:
              payload?.nextTierThreshold ?? prev?.nextTierThreshold,
            nextTierPercentage:
              payload?.nextTierPercentage ?? prev?.nextTierPercentage,
            serviceCommission:
              payload?.serviceCommission ?? prev.serviceCommission,
            valueAddedCommission:
              payload?.valueAddedCommission ?? prev.valueAddedCommission,
            productSalesCommission:
              payload?.productSalesCommission ?? prev.productSalesCommission,
            classTicketCommission:
              payload?.classTicketCommission ?? prev.classTicketCommission,
            classTicketPerformance:
              payload?.classTicketPerformance ?? prev.classTicketPerformance,
            totalCommission: payload?.totalCommission ?? prev.totalCommission,
            performanceBreakdown:
              payload?.performanceBreakdown ?? prev.performanceBreakdown,
            servicePerformance:
              payload?.servicePerformance ?? prev.servicePerformance,
            valueAddedPerformance:
              payload?.valueAddedPerformance ?? prev.valueAddedPerformance,
            productPerformance:
              payload?.productPerformance ?? prev.productPerformance,
            baseSalary: payload?.baseSalary ?? prev.baseSalary,
            usedGuarantee: payload?.usedGuarantee ?? prev.usedGuarantee
          }
        })
      } catch (error) {
        console.error('取得薪資詳細資料失敗', error)
        message.error('無法取得薪資詳細資料，請稍後再試')
        // 不要清空 detailData，保留列表數據中的基本字段
      } finally {
        setDetailLoading(false)
      }
    },
    [businessId]
  )

  const handleViewDetails = useCallback(
    (record) => {
      setSelectedEmployee(record)
      setDetailModalVisible(true)
      // 先使用列表數據中的字段填充詳細數據，確保基本字段有值
      const initialDetailData = {
        employeeId: record.employeeId,
        employeeName: record.employeeName,
        month: record.month,
        guaranteedSalary: record.guaranteedSalary,
        totalSales: record.totalSales,
        tier: record.tier,
        tierPercentage: record.tierPercentage,
        nextTierThreshold: record.nextTierThreshold,
        nextTierPercentage: record.nextTierPercentage,
        serviceCommission: record.serviceCommission,
        valueAddedCommission: record.valueAddedCommission,
        productSalesCommission: record.productSalesCommission,
        classTicketCommission: record.classTicketCommission,
        classTicketPerformance: record.classTicketPerformance,
        totalCommission: record.totalCommission,
        performanceBreakdown: record.performanceBreakdown,
        servicePerformance: record.servicePerformance,
        valueAddedPerformance: record.valueAddedPerformance,
        productPerformance: record.productPerformance,
        baseSalary: record.baseSalary,
        usedGuarantee: record.usedGuarantee,
        additionsTotal: record.additionsTotal,
        deductionsTotal: record.deductionsTotal,
        netSalary: record.netSalary
      }
      setDetailData(initialDetailData)
      // 然後從 API 獲取詳細數據（加項、減項明細等）
      fetchSalaryDetails(record.employeeId, selectedMonth)
      fetchAdjustments(record.employeeId, selectedMonth)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchSalaryDetails, selectedMonth]
  )

  // ── 手動加項／減項 ──
  const canManageAdjustments =
    isOwner || permissionList.includes('finance:manage')

  const fetchAdjustments = useCallback(
    async (employeeId, monthValue) => {
      if (!businessId || !employeeId || !monthValue || !canManageAdjustments)
        return
      try {
        setAdjustmentsLoading(true)
        const res = await api.get(
          `/api/businesses/${businessId}/commission/salary-adjustments`,
          {
            params: { employeeId, month: monthValue.format('YYYY-MM') }
          }
        )
        setAdjustments(res.data?.data || [])
      } catch (error) {
        console.error('取得手動加減項失敗', error)
      } finally {
        setAdjustmentsLoading(false)
      }
    },
    [businessId, canManageAdjustments]
  )

  const refreshAfterAdjustmentChange = useCallback(async () => {
    if (selectedEmployee) {
      fetchSalaryDetails(selectedEmployee.employeeId, selectedMonth)
      fetchAdjustments(selectedEmployee.employeeId, selectedMonth)
    }
    // 列表的實際提撥薪資一併更新
    fetchMonthlySalary(selectedMonth)
  }, [
    selectedEmployee,
    selectedMonth,
    fetchSalaryDetails,
    fetchAdjustments,
    fetchMonthlySalary
  ])

  const handleCreateAdjustment = async () => {
    try {
      const values = await adjustForm.validateFields()
      setAdjustSubmitting(true)
      await api.post(
        `/api/businesses/${businessId}/commission/salary-adjustments`,
        {
          employeeId: selectedEmployee?.employeeId,
          month: selectedMonth.format('YYYY-MM'),
          type: values.type,
          amount: values.amount,
          description: values.description || '',
          date: values.date ? values.date.format('YYYY-MM-DD') : undefined
        }
      )
      message.success('已新增加減項')
      setAdjustModalVisible(false)
      adjustForm.resetFields()
      refreshAfterAdjustmentChange()
    } catch (error) {
      if (error?.errorFields) return // 表單驗證錯誤
      console.error('新增加減項失敗', error)
      message.error(error.response?.data?.message || '新增失敗，請稍後再試')
    } finally {
      setAdjustSubmitting(false)
    }
  }

  const handleDeleteAdjustment = async (adjustmentId) => {
    try {
      await api.delete(
        `/api/businesses/${businessId}/commission/salary-adjustments/${adjustmentId}`
      )
      message.success('已刪除')
      refreshAfterAdjustmentChange()
    } catch (error) {
      console.error('刪除加減項失敗', error)
      message.error(error.response?.data?.message || '刪除失敗，請稍後再試')
    }
  }

  const handleCloseDetailModal = () => {
    setDetailModalVisible(false)
    setSelectedEmployee(null)
    setDetailData(null)
    setAdditionsCategoryFilter('')
    setAdditionsItemFilter([])
    setDeductionsCategoryFilter('')
  }

  const handleViewItemDetail = (item) => {
    setSelectedItem(item)
    setItemDetailModalVisible(true)
  }

  const handleCloseItemDetailModal = () => {
    setItemDetailModalVisible(false)
    setSelectedItem(null)
  }

  const handleMonthChange = (value) => {
    setSelectedMonth(value || tzNow())
  }

  const handleRefresh = () => {
    fetchMonthlySalary(selectedMonth, false)
  }

  /** 依 API 回傳資料匯出 CSV：類別、項目名稱、抽成類型、日期、價格、金額，並含每位員工與全體總計 */
  const handleExportCsv = useCallback(async () => {
    if (!businessId) return
    const monthStr = selectedMonth.format('YYYY-MM')
    try {
      setExportCsvLoading(true)
      const listRes = await api.get(
        `/api/businesses/${businessId}/commission/monthly-salary`,
        {
          params: { month: monthStr, recalculate: 'false' }
        }
      )
      const rawList =
        listRes.data?.data ||
        listRes.data?.salary ||
        listRes.data?.records ||
        listRes.data ||
        []
      const list = normalizeSalaryItems(rawList)

      const escapeCsv = (v) => {
        const s = v === null || v === undefined ? '' : String(v)
        if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
        return s
      }
      const header = [
        '員工',
        '類別',
        '項目名稱',
        '抽成類型',
        '日期',
        '價格',
        '金額'
      ]
      const rows = [header.map(escapeCsv)]

      let grandAddTotal = 0
      let grandDedTotal = 0
      let grandNet = 0

      for (const emp of list) {
        const employeeId =
          emp.employeeId || emp.employee?._id || emp._id || null
        const employeeName =
          emp.employeeName || emp.name || emp.employee?.name || '未命名員工'
        if (!employeeId) {
          rows.push([
            escapeCsv(employeeName),
            '',
            '(無員工ID，略過明細)',
            '',
            '',
            '',
            ''
          ])
          continue
        }
        const detailRes = await api.get(
          `/api/businesses/${businessId}/commission/monthly-salary/details`,
          { params: { employeeId, month: monthStr } }
        )
        const detail =
          detailRes.data?.data ||
          detailRes.data?.details ||
          detailRes.data ||
          {}
        const additions =
          detail.additions?.items ||
          (Array.isArray(detail.additions) ? detail.additions : [])
        const deductions =
          detail.deductions?.items ||
          (Array.isArray(detail.deductions) ? detail.deductions : [])

        const pickDetails = (i) => i?.details || i || {}
        const priceFromItem = (i) => {
          const d = pickDetails(i)
          const n = Number(
            d.serviceAmount ?? d.productAmount ?? d.productSalesAmount ?? ''
          )
          return Number.isFinite(n) ? n : ''
        }
        const commissionTypeFromItem = (i) => {
          const d = pickDetails(i)
          return d.commissionTypeDisplay ?? d.commissionType ?? ''
        }

        for (const i of additions) {
          const amt = Number(i.amount ?? i.value ?? 0)
          grandAddTotal += amt
          rows.push([
            escapeCsv(employeeName),
            escapeCsv(i.category ?? i.type ?? ''),
            escapeCsv(i.description ?? i.name ?? ''),
            escapeCsv(commissionTypeFromItem(i)),
            escapeCsv(i.date ?? ''),
            escapeCsv(priceFromItem(i)),
            escapeCsv(amt)
          ])
        }
        for (const i of deductions) {
          const amt = Number(i.amount ?? i.value ?? 0)
          grandDedTotal += amt
          rows.push([
            escapeCsv(employeeName),
            escapeCsv(i.category ?? i.type ?? ''),
            escapeCsv(i.description ?? i.name ?? ''),
            escapeCsv(commissionTypeFromItem(i)),
            escapeCsv(i.date ?? ''),
            escapeCsv(priceFromItem(i)),
            escapeCsv(amt ? -amt : 0)
          ])
        }
        const addTotal =
          detail.additions?.total ??
          detail.additionsTotal ??
          detail.totalAdditions ??
          0
        const dedTotal =
          detail.deductions?.total ??
          detail.deductionsTotal ??
          detail.totalDeductions ??
          0
        const net =
          detail.netSalary ?? detail.actualSalary ?? detail.amount ?? 0
        grandNet += Number(net)
        rows.push([
          escapeCsv(employeeName),
          escapeCsv('加項總計'),
          '',
          '',
          '',
          '',
          escapeCsv(Number(addTotal))
        ])
        rows.push([
          escapeCsv(employeeName),
          escapeCsv('減項總計'),
          '',
          '',
          '',
          '',
          escapeCsv(-Number(dedTotal))
        ])
        rows.push([
          escapeCsv(employeeName),
          escapeCsv('實際提撥'),
          '',
          '',
          '',
          '',
          escapeCsv(Number(net))
        ])
      }

      rows.push([])
      rows.push([
        escapeCsv('【全體總計】'),
        escapeCsv('加項總計'),
        '',
        '',
        '',
        '',
        escapeCsv(grandAddTotal)
      ])
      rows.push([
        '',
        escapeCsv('減項總計'),
        '',
        '',
        '',
        '',
        escapeCsv(-grandDedTotal)
      ])
      rows.push([
        '',
        escapeCsv('實際提撥合計'),
        '',
        '',
        '',
        '',
        escapeCsv(grandNet)
      ])

      const csvContent =
        '\uFEFF' +
        rows.map((r) => (Array.isArray(r) ? r.join(',') : r)).join('\r\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `薪資明細_${monthStr}_匯出.csv`
      a.click()
      URL.revokeObjectURL(url)
      message.success('已依 API 資料匯出 CSV')
    } catch (err) {
      console.error('匯出 CSV 失敗', err)
      message.error(
        err?.response?.data?.message || err?.message || '匯出失敗，請稍後再試'
      )
    } finally {
      setExportCsvLoading(false)
    }
  }, [businessId, selectedMonth])

  const handleRecalculate = async () => {
    try {
      setLoading(true)
      message.loading('正在重新計算薪資，請稍候...', 0)
      await fetchMonthlySalary(selectedMonth, true)
      message.destroy()
      message.success('重新計算完成！薪資數據已更新')
    } catch (error) {
      message.destroy()
      console.error('重新計算失敗:', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '重新計算失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  const visibleRecords = useMemo(() => {
    if (isOwner) return records
    if (!records.length) return []

    const byId =
      currentUserId !== null
        ? records.filter((item) => item.employeeId === currentUserId)
        : []
    if (byId.length > 0) return byId

    if (userNameCandidates.length === 0) return []

    return records.filter((item) =>
      userNameCandidates.includes(
        String(item.employeeName || '')
          .trim()
          .toLowerCase()
      )
    )
  }, [isOwner, records, currentUserId, userNameCandidates])

  const totalAdditions = useMemo(
    () =>
      visibleRecords.reduce(
        (sum, item) => sum + Number(item.additionsTotal || 0),
        0
      ),
    [visibleRecords]
  )

  const totalDeductions = useMemo(
    () =>
      visibleRecords.reduce(
        (sum, item) => sum + Number(item.deductionsTotal || 0),
        0
      ),
    [visibleRecords]
  )

  const totalNetSalary = useMemo(
    () =>
      visibleRecords.reduce(
        (sum, item) => sum + Number(item.netSalary || 0),
        0
      ),
    [visibleRecords]
  )

  // 業績階梯：以「抽 X%」為主顯示，階梯序為輔
  const getTierDisplay = (tier, tierPercentage) => {
    if (tier === 0) return { main: '保底制', sub: null }
    const tierNames = { 1: '第一階', 2: '第二階', 3: '第三階' }
    const sub = tierNames[tier] || `第 ${tier} 階`
    return {
      main: `抽 ${tierPercentage}%`,
      sub
    }
  }

  // 計算「差多少可達下一階」提示（需 API 回傳 nextTierThreshold、nextTierPercentage）
  const getNextTierHint = (record) => {
    const totalSales = Number(record.totalSales ?? 0) || 0
    const nextThreshold =
      record.nextTierThreshold != null
        ? Number(record.nextTierThreshold)
        : null
    const nextPct =
      record.nextTierPercentage != null
        ? Number(record.nextTierPercentage)
        : null
    if (nextThreshold == null || nextPct == null || nextThreshold <= totalSales)
      return null
    const gap = nextThreshold - totalSales
    return { gap, nextPct }
  }

  // 有任何員工有自收項目時，列表才多一欄「自收應繳店家」
  const hasSelfCollected = useMemo(
    () => records.some((r) => r.selfCollected && r.selfCollected.amount > 0),
    [records]
  )

  const columns = useMemo(() => {
    // 分類抽成顯示：有業績但抽成為 0（該分類未設定抽成規則）時顯示「—」，
    // 避免保底制／業績階梯店家看到滿版 NT$0 誤以為薪資算錯——
    // 這類店家的實際提撥薪資由保底金額或業績階梯計算，本來就不走分類抽成
    const renderCategoryCommission = (value, performance) => {
      const val = Number(value) || 0
      const perf = Number(performance) || 0
      return (
        <div>
          {val === 0 && perf > 0 ? (
            <Tooltip title='此分類未設定逐項抽成規則，不計入分類抽成。保底制／業績階梯員工的薪資以保底金額或階梯比例計算，屬正常顯示。'>
              <AmountText style={{ color: '#999', cursor: 'help' }}>
                —
              </AmountText>
            </Tooltip>
          ) : (
            <AmountText>{formatCurrency(val)}</AmountText>
          )}
          {performance !== undefined && (
            <PerformanceText>業績:{formatCurrency(perf)}</PerformanceText>
          )}
        </div>
      )
    }

    // 手機版只顯示：員工名稱、實際提撥薪資、操作
    if (isMobile) {
      return [
        {
          title: '員工名稱',
          dataIndex: 'employeeName',
          key: 'employeeName',
          width: 150,
          ellipsis: {
            showTitle: true
          }
        },
        {
          title: '實際提撥薪資',
          dataIndex: 'netSalary',
          key: 'netSalary',
          align: 'right',
          width: 150,
          render: (value) => (
            <AmountText style={{ fontWeight: 600 }}>
              {formatCurrency(value)}
            </AmountText>
          )
        },
        {
          title: '操作',
          key: 'action',
          width: 100,
          align: 'center',
          render: (_, record) => (
            <Button
              type='link'
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              size='small'
            >
              查看細項
            </Button>
          )
        }
      ]
    }

    // 桌面版顯示所有列
    return [
      {
        title: '員工名稱',
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: 180,
        fixed: 'left',
        ellipsis: {
          showTitle: true
        }
      },
      {
        title: '業績階梯',
        key: 'tier',
        width: 160,
        render: (_, record) => {
          const tier = record.tier ?? 0
          const tierPercentage = record.tierPercentage ?? 0
          const { main, sub } = getTierDisplay(tier, tierPercentage)
          const nextHint = getNextTierHint(record)
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {tier === 0 ? (
                <TierDisplayGuarantee>{main}</TierDisplayGuarantee>
              ) : (
                <TierDisplayWrap>
                  <TierDisplayMain>{main}</TierDisplayMain>
                  {sub && <TierDisplaySub>{sub}</TierDisplaySub>}
                </TierDisplayWrap>
              )}
              {nextHint && (
                <span style={{ fontSize: 12, color: colors.text.secondary }}>
                  再 {formatCurrency(nextHint.gap)} 可達下一階 {nextHint.nextPct}%
                </span>
              )}
            </div>
          )
        }
      },
      {
        title: '保底金額',
        dataIndex: 'guaranteedSalary',
        key: 'guaranteedSalary',
        align: 'right',
        width: 120,
        render: (value) => <AmountText>{formatCurrency(value)}</AmountText>
      },
      {
        title: '總業績',
        dataIndex: 'totalSales',
        key: 'totalSales',
        align: 'right',
        width: 120,
        render: (value) => <AmountText>{formatCurrency(value)}</AmountText>
      },
      {
        title: <span>技術服務抽成</span>,
        dataIndex: 'serviceCommission',
        key: 'serviceCommission',
        align: 'right',
        width: 140,
        render: (value, record) =>
          renderCategoryCommission(
            value,
            record.servicePerformance ??
              record.performanceBreakdown?.servicePerformance
          )
      },
      {
        title: <span>儲值抽成</span>,
        dataIndex: 'valueAddedCommission',
        key: 'valueAddedCommission',
        align: 'right',
        width: 120,
        render: (value, record) =>
          renderCategoryCommission(
            value,
            record.valueAddedPerformance ??
              record.performanceBreakdown?.valueAddedPerformance
          )
      },
      {
        title: <span>產品銷售抽成</span>,
        dataIndex: 'productSalesCommission',
        key: 'productSalesCommission',
        align: 'right',
        width: 140,
        render: (value, record) =>
          renderCategoryCommission(
            value,
            record.productPerformance ??
              record.performanceBreakdown?.productPerformance
          )
      },
      {
        title: <span>堂票銷售抽成</span>,
        dataIndex: 'classTicketCommission',
        key: 'classTicketCommission',
        align: 'right',
        width: 140,
        render: (value, record) =>
          renderCategoryCommission(
            value,
            record.classTicketPerformance ??
              record.performanceBreakdown?.classTicketPerformance
          )
      },
      ...(hasSelfCollected
        ? [
            {
              title: (
                <Tooltip title='客人直接付給員工的項目，不計入薪資；這裡是員工當月應繳回店家的金額'>
                  <span>自收應繳店家</span>
                </Tooltip>
              ),
              dataIndex: 'selfCollected',
              key: 'selfCollected',
              align: 'right',
              width: 140,
              render: (sc) =>
                sc && sc.amount > 0 ? (
                  <div>
                    <AmountText style={{ fontWeight: 600 }}>
                      {formatCurrency(sc.storeShare)}
                    </AmountText>
                    <PerformanceText>
                      收費:{formatCurrency(sc.amount)}
                    </PerformanceText>
                  </div>
                ) : (
                  '—'
                )
            }
          ]
        : []),
      {
        title: '總抽成',
        dataIndex: 'totalCommission',
        key: 'totalCommission',
        align: 'right',
        width: 120,
        render: (value) => (
          <AmountText style={{ fontWeight: 600 }}>
            {formatCurrency(value)}
          </AmountText>
        )
      },
      {
        title: '基礎薪資',
        dataIndex: 'baseSalary',
        key: 'baseSalary',
        align: 'right',
        width: 120,
        render: (value, record) => (
          <div>
            <AmountText style={{ fontWeight: 600 }}>
              {formatCurrency(value)}
            </AmountText>
            {record.usedGuarantee && (
              <Tag color='orange' size='small' style={{ marginTop: 4 }}>
                使用保底
              </Tag>
            )}
          </div>
        )
      },
      {
        title: '加項總計',
        dataIndex: 'additionsTotal',
        key: 'additionsTotal',
        align: 'right',
        width: 120,
        render: (value) => <AmountText>{formatCurrency(value)}</AmountText>
      },
      {
        title: '減項總計',
        dataIndex: 'deductionsTotal',
        key: 'deductionsTotal',
        align: 'right',
        width: 120,
        render: (value) => (
          <AmountText negative>{formatCurrency(value)}</AmountText>
        )
      },
      {
        title: '實際提撥薪資',
        dataIndex: 'netSalary',
        key: 'netSalary',
        align: 'right',
        width: 140,
        fixed: 'right',
        render: (value) => (
          <AmountText style={{ fontWeight: 600, fontSize: '16px' }}>
            {formatCurrency(value)}
          </AmountText>
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            size='small'
          >
            查看細項
          </Button>
        )
      }
    ]
  }, [isMobile, handleViewDetails, hasSelfCollected])

  if (detailModalVisible) {
    const additionsItems =
      detailData?.additions?.items ||
      (Array.isArray(detailData?.additions) ? detailData.additions : [])
    const additionsTotal =
      detailData?.additions?.total ??
      detailData?.additionsTotal ??
      detailData?.totalAdditions ??
      0

    const deductionsItems =
      detailData?.deductions?.items ||
      (Array.isArray(detailData?.deductions) ? detailData.deductions : [])
    const deductionsTotal =
      detailData?.deductions?.total ??
      detailData?.deductionsTotal ??
      detailData?.totalDeductions ??
      0

    const additionCategories = [
      ...new Set(additionsItems.map((i) => i.category || i.type || '加項項目'))
    ]
      .filter(Boolean)
      .sort()
    const deductionCategories = [
      ...new Set(deductionsItems.map((i) => i.category || i.type || '減項項目'))
    ]
      .filter(Boolean)
      .sort()
    // 項目名稱：服務／商品／堂票範本名稱，不含客人姓名，同一項目才能合在一起篩
    const additionItemNameOf = (i) => {
      const d = i.details || {}
      return (
        d.serviceName ||
        d.serviceItems?.[0]?.serviceName ||
        d.productName ||
        d.templateName ||
        i.description ||
        i.name ||
        '加項項目'
      )
    }
    const categoryFilteredAdditions = additionsCategoryFilter
      ? additionsItems.filter(
          (i) =>
            (i.category || i.type || '加項項目') === additionsCategoryFilter
        )
      : additionsItems
    // 項目名稱選項跟著類別篩選走，並附上筆數
    const additionItemOptions = Object.entries(
      categoryFilteredAdditions.reduce((acc, i) => {
        const n = additionItemNameOf(i)
        acc[n] = (acc[n] || 0) + 1
        return acc
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ value: name, label: `${name}（${count}）` }))
    const filteredAdditionsItems = additionsItemFilter.length
      ? categoryFilteredAdditions.filter((i) =>
          additionsItemFilter.includes(additionItemNameOf(i))
        )
      : categoryFilteredAdditions
    const hasAdditionsFilter =
      !!additionsCategoryFilter || additionsItemFilter.length > 0
    const filteredDeductionsItems = deductionsCategoryFilter
      ? deductionsItems.filter(
          (i) =>
            (i.category || i.type || '減項項目') === deductionsCategoryFilter
        )
      : deductionsItems
    const filteredAdditionsTotal = filteredAdditionsItems.reduce(
      (s, i) => s + Number(i.amount ?? i.value ?? 0),
      0
    )
    const filteredDeductionsTotal = filteredDeductionsItems.reduce(
      (s, i) => s + Number(i.amount ?? i.value ?? 0),
      0
    )

    const selfCollectedRecords = detailData?.selfCollected?.records ?? []
    const selfCollectedTotals = detailData?.selfCollected?.totals ?? {}
    const valueAddedDetail = detailData?.valueAddedDetail ?? {}
    const vadRecords = valueAddedDetail.records ?? []
    const byCategory = valueAddedDetail.byCategory ?? {}
    const purchaseRecords =
      byCategory.purchase ?? vadRecords.filter((r) => r.source === 'purchase')
    const topupRecords =
      byCategory.topup ??
      vadRecords.filter((r) => r.source === 'topup') ??
      valueAddedDetail.topupOnlyRecords ??
      []

    const baseSalary =
      detailData?.baseSalary ??
      detailData?.salary ??
      detailData?.fixedSalary ??
      0
    const netSalary =
      detailData?.netSalary ??
      detailData?.actualSalary ??
      detailData?.amount ??
      0

    return (
      <DetailPageWrapper>
        <DetailHeader>
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              position: 'relative',
              zIndex: 1
            }}
          >
            <BackButton
              icon={<ArrowLeftOutlined />}
              onClick={handleCloseDetailModal}
            >
              返回列表
            </BackButton>
            <div style={{ marginTop: 16 }}>
              <Title
                level={2}
                style={{
                  color: colors.text.primary,
                  marginBottom: spacing.sm,
                  fontSize: 28,
                  fontWeight: 300
                }}
                className='detail-title'
              >
                {selectedEmployee?.employeeName || '員工'} 薪資詳細資料
              </Title>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                  color: colors.text.secondary,
                  fontSize: 14
                }}
                className='detail-meta'
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs
                  }}
                >
                  <CalendarIcon />
                  <span>{selectedMonth.format('YYYY年MM月')}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs
                  }}
                >
                  <UserOutlined />
                  <span>{selectedEmployee?.employeeName || '--'}</span>
                </div>
              </div>
            </div>
          </div>
        </DetailHeader>

        <DetailContent>
          <SummaryCard>
            <SummaryItem>
              <SummaryLabel>保底金額</SummaryLabel>
              <SummaryValue>
                {formatCurrency(
                  detailData?.guaranteedSalary ?? detailData?.salary ?? 0
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>總業績</SummaryLabel>
              <SummaryValue>
                {formatCurrency(detailData?.totalSales ?? 0)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>業績階梯</SummaryLabel>
              <SummaryValue style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {detailData?.tier !== undefined &&
                detailData?.tierPercentage !== undefined ? (
                  (() => {
                    const tierDisplay = getTierDisplay(detailData.tier, detailData.tierPercentage)
                    const nextHint = getNextTierHint(detailData)
                    return (
                      <>
                        {detailData.tier === 0 ? (
                          <TierDisplayGuarantee>{tierDisplay.main}</TierDisplayGuarantee>
                        ) : (
                          <TierDisplayWrap>
                            <TierDisplayMain>{tierDisplay.main}</TierDisplayMain>
                            {tierDisplay.sub && (
                              <TierDisplaySub>{tierDisplay.sub}</TierDisplaySub>
                            )}
                          </TierDisplayWrap>
                        )}
                        {nextHint && (
                          <span style={{ fontSize: 12, color: colors.text.secondary }}>
                            再 {formatCurrency(nextHint.gap)} 可達下一階 {nextHint.nextPct}%
                          </span>
                        )}
                      </>
                    )
                  })()
                ) : (
                  '--'
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>技術服務抽成</SummaryLabel>
              <SummaryValue style={{ color: colors.success }}>
                {formatCurrency(detailData?.serviceCommission ?? 0)}
                {(detailData?.servicePerformance !== undefined ||
                  detailData?.performanceBreakdown?.servicePerformance !==
                    undefined) && (
                  <PerformanceText style={{ textAlign: 'center' }}>
                    業績:
                    {formatCurrency(
                      detailData.servicePerformance ??
                        detailData.performanceBreakdown?.servicePerformance
                    )}
                  </PerformanceText>
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>儲值抽成</SummaryLabel>
              <SummaryValue style={{ color: colors.success }}>
                {formatCurrency(detailData?.valueAddedCommission ?? 0)}
                {(detailData?.valueAddedPerformance !== undefined ||
                  detailData?.performanceBreakdown?.valueAddedPerformance !==
                    undefined) && (
                  <PerformanceText style={{ textAlign: 'center' }}>
                    業績:
                    {formatCurrency(
                      detailData.valueAddedPerformance ??
                        detailData.performanceBreakdown?.valueAddedPerformance
                    )}
                  </PerformanceText>
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>產品銷售抽成</SummaryLabel>
              <SummaryValue style={{ color: colors.success }}>
                {formatCurrency(detailData?.productSalesCommission ?? 0)}
                {(detailData?.productPerformance !== undefined ||
                  detailData?.performanceBreakdown?.productPerformance !==
                    undefined) && (
                  <PerformanceText style={{ textAlign: 'center' }}>
                    業績:
                    {formatCurrency(
                      detailData.productPerformance ??
                        detailData.performanceBreakdown?.productPerformance
                    )}
                  </PerformanceText>
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>堂票銷售抽成</SummaryLabel>
              <SummaryValue style={{ color: colors.success }}>
                {formatCurrency(detailData?.classTicketCommission ?? 0)}
                {(detailData?.classTicketPerformance !== undefined ||
                  detailData?.performanceBreakdown?.classTicketPerformance !==
                    undefined) && (
                  <PerformanceText style={{ textAlign: 'center' }}>
                    業績:
                    {formatCurrency(
                      detailData.classTicketPerformance ??
                        detailData.performanceBreakdown?.classTicketPerformance
                    )}
                  </PerformanceText>
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>總抽成</SummaryLabel>
              <SummaryValue style={{ color: colors.success, fontWeight: 600 }}>
                {formatCurrency(detailData?.totalCommission ?? 0)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>
                基礎薪資
                {detailData?.usedGuarantee && (
                  <Tag color='orange' size='small' style={{ marginLeft: 8 }}>
                    使用保底
                  </Tag>
                )}
              </SummaryLabel>
              <SummaryValue style={{ fontWeight: 600 }}>
                {formatCurrency(
                  detailData?.baseSalary ??
                    detailData?.guaranteedSalary ??
                    detailData?.salary ??
                    0
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>加項總計</SummaryLabel>
              <SummaryValue style={{ color: colors.success }}>
                {/* 與列表頁同語意：零用金等「額外加項」（summary.additionsTotal）。
                    抽成明細合計顯示在「總抽成」卡與下方加項明細區，
                    不可再與基礎薪資相加（保底制已於基礎薪資取 max 判定） */}
                {formatCurrency(detailData?.additionsTotal ?? 0)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>減項總計</SummaryLabel>
              <SummaryValue style={{ color: colors.error }}>
                {formatCurrency(deductionsTotal)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem highlight>
              <SummaryLabel>實際提撥薪資</SummaryLabel>
              <SummaryValue
                style={{ color: colors.text.primary, fontWeight: 600 }}
              >
                {formatCurrency(netSalary)}
              </SummaryValue>
            </SummaryItem>
          </SummaryCard>

          {selfCollectedRecords.length > 0 && (
            <SectionCard>
              <SectionTitle>
                <WalletOutlined style={{ color: colors.text.secondary }} />
                自收項目拆帳（不計入薪資）
              </SectionTitle>
              <Hint tone='info'>
                這些項目客人是直接付給{selectedEmployee?.employeeName || '員工'}，
                不算在上面的薪資裡。月底由本人把「應繳店家」的金額交給店家。
                用堂票做的那次，以堂票售價 ÷ 總堂數當作這次的收費。
              </Hint>
              <SummaryCard style={{ marginTop: 12 }}>
                <SummaryItem>
                  <SummaryLabel>本月收費</SummaryLabel>
                  <SummaryValue>
                    {formatCurrency(
                      (selfCollectedTotals.serviceAmount || 0) +
                        (selfCollectedTotals.redeemAmount || 0)
                    )}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>本人所得</SummaryLabel>
                  <SummaryValue>
                    {formatCurrency(selfCollectedTotals.employeeShare || 0)}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem highlight>
                  <SummaryLabel>應繳店家</SummaryLabel>
                  <SummaryValue
                    style={{ color: colors.text.primary, fontWeight: 600 }}
                  >
                    {formatCurrency(selfCollectedTotals.storeShare || 0)}
                  </SummaryValue>
                </SummaryItem>
                {(selfCollectedTotals.ticketSalesAmount || 0) > 0 && (
                  <SummaryItem>
                    <SummaryLabel>本月售出自收堂票</SummaryLabel>
                    <SummaryValue>
                      {formatCurrency(selfCollectedTotals.ticketSalesAmount)}
                    </SummaryValue>
                  </SummaryItem>
                )}
              </SummaryCard>
              <StyledTable
                dataSource={selfCollectedRecords.map((r, i) => ({
                  ...r,
                  key: `${r.type}-${r.orderId || r.ticketCode || ''}-${i}`
                }))}
                pagination={false}
                size='small'
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: '日期',
                    dataIndex: 'date',
                    key: 'date',
                    width: 110,
                    render: (d) => d || '—'
                  },
                  {
                    title: '客戶',
                    dataIndex: 'customerName',
                    key: 'customerName',
                    render: (t) => t || '—'
                  },
                  {
                    title: '項目',
                    dataIndex: 'serviceName',
                    key: 'serviceName',
                    render: (t, r) => (
                      <div>
                        <div>{t || '—'}</div>
                        {r.type === 'ticket_redeem' && (
                          <Tag color='purple' style={{ marginTop: 4 }}>
                            堂票 {r.ticketCode} ・每堂 {formatCurrency(r.perSessionValue)}
                          </Tag>
                        )}
                        {r.type === 'ticket_sale' && (
                          <Tag style={{ marginTop: 4 }}>
                            售出堂票 {r.ticketCode}（核銷時再繳）
                          </Tag>
                        )}
                      </div>
                    )
                  },
                  {
                    title: '收費',
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 110,
                    align: 'right',
                    render: (v) => formatCurrency(v || 0)
                  },
                  {
                    title: '本人所得',
                    dataIndex: 'employeeShare',
                    key: 'employeeShare',
                    width: 110,
                    align: 'right',
                    render: (v) => formatCurrency(v || 0)
                  },
                  {
                    title: '應繳店家',
                    dataIndex: 'storeShare',
                    key: 'storeShare',
                    width: 120,
                    align: 'right',
                    render: (v, r) =>
                      r.type === 'ticket_sale' ? (
                        '—'
                      ) : (
                        <span style={{ fontWeight: 600 }}>
                          {formatCurrency(v || 0)}
                          <span
                            style={{
                              color: colors.text.secondary,
                              fontWeight: 400,
                              fontSize: 12,
                              marginLeft: 4
                            }}
                          >
                            ({r.storeShareRate}%)
                          </span>
                        </span>
                      )
                  }
                ]}
              />
            </SectionCard>
          )}

          <SectionCard>
            <SectionTitle>
              <WalletOutlined style={{ color: colors.success }} />
              加項明細
            </SectionTitle>
            <Spin spinning={detailLoading}>
              {additionsItems.length > 0 ? (
                <>
                  <div
                    style={{
                      marginBottom: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ color: colors.text.secondary }}>
                      類別篩選：
                    </span>
                    <Select
                      value={additionsCategoryFilter || undefined}
                      onChange={(v) => {
                        setAdditionsCategoryFilter(v || '')
                        setAdditionsItemFilter([])
                      }}
                      style={{ width: 180 }}
                      placeholder='全部'
                      allowClear
                      options={[
                        { value: '', label: '全部' },
                        ...additionCategories.map((c) => ({
                          value: c,
                          label: c
                        }))
                      ]}
                    />
                    <span style={{ color: colors.text.secondary }}>
                      項目：
                    </span>
                    <Select
                      mode='multiple'
                      value={additionsItemFilter}
                      onChange={(v) => setAdditionsItemFilter(v || [])}
                      style={{ minWidth: 240, maxWidth: '100%' }}
                      placeholder='全部項目'
                      allowClear
                      maxTagCount='responsive'
                      optionFilterProp='label'
                      options={additionItemOptions}
                    />
                    {hasAdditionsFilter && (
                      <span
                        style={{ fontSize: 13, color: colors.text.tertiary }}
                      >
                        共 {filteredAdditionsItems.length} 筆
                      </span>
                    )}
                  </div>
                  <StyledTable
                    dataSource={filteredAdditionsItems.map((item, index) => ({
                      ...item,
                      key: index
                    }))}
                    columns={[
                      {
                        title: '類別',
                        dataIndex: 'category',
                        key: 'category',
                        width: 120,
                        render: (_, record) => {
                          const category =
                            record.category || record.type || '加項項目'
                          // 根據類別決定標籤顏色
                          let badgeColor = 'success' // 預設綠色（其他加項）
                          if (
                            category.includes('技術服務') ||
                            category.includes('技术服务') ||
                            category === 'serviceCommission' ||
                            category === '技術服務'
                          ) {
                            badgeColor = 'blue'
                          } else if (
                            category.includes('儲值') ||
                            category.includes('储值') ||
                            category === 'valueAddedCommission' ||
                            category === '儲值'
                          ) {
                            badgeColor = 'green'
                          } else if (
                            category.includes('產品銷售') ||
                            category.includes('产品销售') ||
                            category === 'productSalesCommission' ||
                            category === '產品銷售'
                          ) {
                            badgeColor = 'orange'
                          } else if (
                            category.includes('堂票') ||
                            category === 'classTicketCommission' ||
                            category === '堂票銷售抽成'
                          ) {
                            badgeColor = 'purple'
                          }
                          return <Tag color={badgeColor}>{category}</Tag>
                        }
                      },
                      {
                        title: '項目名稱',
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                        render: (_, record) =>
                          record.description || record.name || '加項項目'
                      },
                      {
                        title: '抽成類型',
                        dataIndex: 'details.commissionTypeDisplay',
                        key: 'details.commissionTypeDisplay',
                        width: 200,
                        render: (_, record) => {
                          const details = record.details || {}
                          const commissionType = details.commissionType

                          // 優先從 serviceItems 數組中獲取數據
                          const serviceItems = details.serviceItems || []
                          const firstServiceItem = serviceItems[0] || {}

                          const isSamePerson =
                            firstServiceItem.isSamePerson ??
                            details.isSamePerson
                          const designerCommission =
                            firstServiceItem.designerCommission ??
                            details.designerCommission
                          const assistantCommission =
                            firstServiceItem.assistantCommission ??
                            details.assistantCommission
                          const commissionAmount =
                            firstServiceItem.commissionAmount ??
                            details.commissionAmount ??
                            record.amount ??
                            0
                          const upgradeMultiplier =
                            firstServiceItem.upgradeMultiplier ??
                            details.upgradeMultiplier

                          // 如果是設計師和助理抽成類型
                          if (
                            commissionType === 'designer_and_assistant' &&
                            (designerCommission !== undefined ||
                              assistantCommission !== undefined)
                          ) {
                            const items = []

                            // 設計師抽成
                            if (designerCommission !== undefined) {
                              let percentage = ''
                              if (commissionAmount > 0 && upgradeMultiplier) {
                                // 計算設計師抽成比例
                                const designerRate = Math.round(
                                  (designerCommission / commissionAmount) * 100
                                )
                                percentage = `（${designerRate}%）`
                              } else if (upgradeMultiplier) {
                                // 使用升級倍數計算：60% = 3 * 20%
                                const baseRate = Math.round(
                                  100 / (upgradeMultiplier + 1)
                                )
                                const designerRate =
                                  baseRate * upgradeMultiplier
                                percentage = `（${designerRate}%）`
                              }
                              items.push(
                                <div key='designer' style={{ marginBottom: 4 }}>
                                  設計師抽成：
                                  {formatCurrency(designerCommission)}
                                  {percentage}
                                </div>
                              )
                            }

                            // 助理抽成
                            if (assistantCommission !== undefined) {
                              let percentage = ''
                              if (commissionAmount > 0 && upgradeMultiplier) {
                                // 計算助理抽成比例
                                const assistantRate = Math.round(
                                  (assistantCommission / commissionAmount) * 100
                                )
                                percentage = `（${assistantRate}%）`
                              } else if (upgradeMultiplier) {
                                // 使用升級倍數計算：20% = 100% / (3 + 1)
                                const assistantRate = Math.round(
                                  100 / (upgradeMultiplier + 1)
                                )
                                percentage = `（${assistantRate}%）`
                              }
                              items.push(
                                <div
                                  key='assistant'
                                  style={{ marginBottom: 4 }}
                                >
                                  助理抽成：
                                  {formatCurrency(assistantCommission)}
                                  {percentage}
                                </div>
                              )
                            }

                            return <div style={{ fontSize: 13 }}>{items}</div>
                          }

                          // 其他情況，顯示原有的抽成類型
                          return (
                            details.commissionTypeDisplay ||
                            details.commissionType ||
                            '抽成'
                          )
                        }
                      },
                      {
                        title: '日期',
                        key: 'date',
                        width: 100,
                        render: (_, record) => (
                          <span>
                            {record.date
                              ? dayjs(record.date).format('MM-DD')
                              : ''}
                          </span>
                        )
                      },
                      {
                        title: '價格',
                        key: 'serviceAmount',
                        align: 'right',
                        width: 120,
                        render: (_, record) => {
                          const details = record.details || {}
                          const isProduct =
                            record.type === 'productSalesCommission' ||
                            (record.category &&
                              record.category.includes('產品銷售'))
                          const isClassTicket =
                            record.type === 'classTicketCommission' ||
                            (record.category &&
                              record.category.includes('堂票'))
                          const amount = details.isTeamBonus
                            ? null
                            : isClassTicket
                            ? details.price
                            : isProduct
                            ? (details.productAmount ??
                              details.productSalesAmount)
                            : details.serviceAmount ||
                              (details.serviceItems &&
                                details.serviceItems[0]?.serviceAmount) ||
                              null
                          return amount !== null && amount !== undefined ? (
                            <span
                              style={{
                                fontSize: 13,
                                color: colors.text.secondary
                              }}
                            >
                              {formatCurrency(Number(amount))}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 13,
                                color: colors.text.tertiary
                              }}
                            >
                              -
                            </span>
                          )
                        }
                      },
                      {
                        title: '金額',
                        dataIndex: 'amount',
                        key: 'amount',
                        align: 'right',
                        width: 100,
                        render: (_, record) => (
                          <ItemAmount>
                            {formatCurrency(
                              Number(record.amount ?? record.value ?? 0)
                            )}
                          </ItemAmount>
                        )
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 80,
                        align: 'center',
                        render: (_, record) => (
                          <Button
                            type='link'
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewItemDetail(record)
                            }}
                          >
                            詳情
                          </Button>
                        )
                      }
                    ]}
                    pagination={false}
                    size='small'
                    scroll={{ x: 'max-content', y: 400 }}
                    onRow={(record) => ({
                      onClick: () => handleViewItemDetail(record)
                    })}
                  />
                  <TotalSection>
                    <TotalLabel>
                      {hasAdditionsFilter ? '篩選小計：' : '加項總計：'}
                    </TotalLabel>
                    <TotalValue>
                      {formatCurrency(
                        hasAdditionsFilter
                          ? filteredAdditionsTotal
                          : additionsTotal
                      )}
                    </TotalValue>
                  </TotalSection>
                </>
              ) : (
                <Empty
                  description='本月尚無加項紀錄'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: '40px 0' }}
                />
              )}
            </Spin>
          </SectionCard>

          {purchaseRecords.length > 0 && (
            <SectionCard>
              <SectionTitle>
                <WalletOutlined style={{ color: colors.success }} />
                儲值銷售
              </SectionTitle>
              <StyledTable
                dataSource={purchaseRecords.map((r, i) => ({
                  ...r,
                  key: r.purchaseId || `p-${i}`
                }))}
                pagination={false}
                size='small'
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: '日期',
                    dataIndex: 'date',
                    key: 'date',
                    width: 110,
                    render: (d) => d || '—'
                  },
                  {
                    title: '客戶',
                    dataIndex: 'customerName',
                    key: 'customerName',
                    ellipsis: true,
                    render: (t) => t || '—'
                  },
                  {
                    title: '電話',
                    dataIndex: 'customerPhone',
                    key: 'customerPhone',
                    width: 120,
                    render: (t) => t || '—'
                  },
                  {
                    title: '金額',
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 100,
                    align: 'right',
                    render: (v) => (v != null ? formatCurrency(v) : '—')
                  },
                  {
                    title: '儲值金額',
                    dataIndex: 'valueAddedAmount',
                    key: 'valueAddedAmount',
                    width: 100,
                    align: 'right',
                    render: (v) => (v != null ? formatCurrency(v) : '—')
                  },
                  {
                    title: '折扣',
                    dataIndex: 'discount',
                    key: 'discount',
                    width: 70,
                    render: (v) => (v != null ? `${v}%` : '—')
                  },
                  {
                    title: '備註',
                    dataIndex: 'note',
                    key: 'note',
                    ellipsis: true,
                    render: (t) => t || '—'
                  },
                  {
                    title: '支付方式',
                    dataIndex: 'paymentMethod',
                    key: 'paymentMethod',
                    width: 90,
                    render: (v) => {
                      const map = { cash: '💵 現金', card: '💳 刷卡', mobile_payment: '📱 行動支付', transfer: '🏦 轉帳' }
                      return map[v] || (v ? v : '—')
                    }
                  },
                  {
                    title: '抽成比例',
                    dataIndex: 'commissionRate',
                    key: 'commissionRate',
                    width: 80,
                    align: 'right',
                    render: (v) => (v != null ? `${v}%` : '—')
                  },
                  {
                    title: '手續費',
                    dataIndex: 'fee',
                    key: 'fee',
                    width: 90,
                    align: 'right',
                    render: (v, record) =>
                      record.commissionRule === 'profit_rate_minus_fee' && v > 0
                        ? <span style={{ color: '#e74c3c' }}>− {formatCurrency(v)}</span>
                        : '—'
                  },
                  {
                    title: '抽成金額',
                    dataIndex: 'commissionAmount',
                    key: 'commissionAmount',
                    width: 90,
                    align: 'right',
                    render: (v) =>
                      v != null
                        ? <span style={{ color: '#27ae60', fontWeight: 600 }}>{formatCurrency(v)}</span>
                        : '—'
                  }
                ]}
              />
            </SectionCard>
          )}

          {topupRecords.length > 0 && (
            <SectionCard>
              <SectionTitle>
                <WalletOutlined style={{ color: colors.text.secondary }} />
                本月儲值明細（不計業績）
              </SectionTitle>
              <StyledTable
                dataSource={topupRecords.map((r, i) => ({
                  ...r,
                  key: r.transactionId || `t-${i}`
                }))}
                pagination={false}
                size='small'
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: '日期',
                    dataIndex: 'date',
                    key: 'date',
                    width: 110,
                    render: (d) => d || '—'
                  },
                  {
                    title: '客戶',
                    dataIndex: 'customerName',
                    key: 'customerName',
                    ellipsis: true,
                    render: (t) => t || '—'
                  },
                  {
                    title: '電話',
                    dataIndex: 'customerPhone',
                    key: 'customerPhone',
                    width: 120,
                    render: (t) => t || '—'
                  },
                  {
                    title: '金額',
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 100,
                    align: 'right',
                    render: (v) => (v != null ? formatCurrency(v) : '—')
                  },
                  {
                    title: '備註',
                    dataIndex: 'note',
                    key: 'note',
                    ellipsis: true,
                    render: (t) => t || '—'
                  }
                ]}
              />
            </SectionCard>
          )}

{canManageAdjustments && (
            <SectionCard>
              <SectionTitle
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8
                }}
              >
                <span>
                  <WalletOutlined style={{ color: colors.success }} />{' '}
                  手動加項／減項
                </span>
                <Button
                  type='primary'
                  size='small'
                  icon={<PlusOutlined />}
                  onClick={() => {
                    adjustForm.resetFields()
                    setAdjustModalVisible(true)
                  }}
                >
                  新增
                </Button>
              </SectionTitle>
              <div
                style={{
                  fontSize: 13,
                  color: colors.text.secondary,
                  margin: '4px 0 12px'
                }}
              >
                獎金、全勤、津貼等額外加給或扣款，直接計入「加項總計／減項總計」。
              </div>
              <Table
                size='small'
                rowKey='_id'
                loading={adjustmentsLoading}
                dataSource={adjustments}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: '本月尚無手動加減項' }}
                columns={[
                  { title: '日期', dataIndex: 'date', width: 110 },
                  {
                    title: '類型',
                    dataIndex: 'type',
                    width: 80,
                    render: (t) =>
                      t === 'add' ? (
                        <Tag color='green'>加項</Tag>
                      ) : (
                        <Tag color='red'>減項</Tag>
                      )
                  },
                  {
                    title: '金額',
                    dataIndex: 'amount',
                    width: 130,
                    render: (v, r) => (
                      <span
                        style={{
                          color: r.type === 'add' ? colors.success : colors.error,
                          fontWeight: 600
                        }}
                      >
                        {r.type === 'add' ? '+' : '−'}
                        {formatCurrency(v)}
                      </span>
                    )
                  },
                  {
                    title: '說明',
                    dataIndex: 'description',
                    render: (v) => v || '—'
                  },
                  {
                    title: '',
                    key: 'actions',
                    width: 60,
                    render: (_, r) => (
                      <Popconfirm
                        title='確定刪除此筆加減項？'
                        okText='刪除'
                        cancelText='取消'
                        onConfirm={() => handleDeleteAdjustment(r._id)}
                      >
                        <Button
                          type='text'
                          danger
                          size='small'
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    )
                  }
                ]}
              />
            </SectionCard>
          )}

          <Modal
            title={`新增加項／減項（${selectedEmployee?.employeeName || ''} ${selectedMonth.format('YYYY 年 MM 月')}）`}
            open={adjustModalVisible}
            onCancel={() => setAdjustModalVisible(false)}
            onOk={handleCreateAdjustment}
            confirmLoading={adjustSubmitting}
            okText='儲存'
            cancelText='取消'
            destroyOnClose
          >
            <Form
              form={adjustForm}
              layout='vertical'
              initialValues={{ type: 'add', date: tzNow() }}
            >
              <Form.Item
                name='type'
                label='類型'
                rules={[{ required: true, message: '請選擇類型' }]}
              >
                <Radio.Group>
                  <Radio.Button value='add'>加項（獎金、津貼…）</Radio.Button>
                  <Radio.Button value='deduct'>減項（扣款…）</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                name='amount'
                label='金額'
                rules={[
                  { required: true, message: '請輸入金額' },
                  {
                    type: 'number',
                    min: 1,
                    message: '金額必須大於 0'
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  precision={0}
                  placeholder='輸入正數，方向由類型決定'
                />
              </Form.Item>
              <Form.Item name='date' label='日期'>
                <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
              </Form.Item>
              <Form.Item name='description' label='說明'>
                <Input
                  maxLength={100}
                  placeholder='例如：全勤獎金、教育訓練津貼、制服代扣'
                />
              </Form.Item>
            </Form>
          </Modal>

          <SectionCard>
            <SectionTitle>
              <WalletOutlined style={{ color: colors.error }} />
              減項明細
            </SectionTitle>
            <Spin spinning={detailLoading}>
              {deductionsItems.length > 0 ? (
                <>
                  <div
                    style={{
                      marginBottom: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ color: colors.text.secondary }}>
                      類別篩選：
                    </span>
                    <Select
                      value={deductionsCategoryFilter || undefined}
                      onChange={(v) => setDeductionsCategoryFilter(v || '')}
                      style={{ width: 180 }}
                      placeholder='全部'
                      allowClear
                      options={[
                        { value: '', label: '全部' },
                        ...deductionCategories.map((c) => ({
                          value: c,
                          label: c
                        }))
                      ]}
                    />
                    {deductionsCategoryFilter && (
                      <span
                        style={{ fontSize: 13, color: colors.text.tertiary }}
                      >
                        共 {filteredDeductionsItems.length} 筆
                      </span>
                    )}
                  </div>
                  <StyledTable
                    dataSource={filteredDeductionsItems.map((item, index) => ({
                      ...item,
                      key: index
                    }))}
                    columns={[
                      {
                        title: '類別',
                        dataIndex: 'category',
                        key: 'category',
                        width: 120,
                        render: (_, record) => {
                          const category =
                            record.category || record.type || '減項項目'
                          // 減項統一使用紅色
                          return <Tag color='red'>{category}</Tag>
                        }
                      },
                      {
                        title: '項目名稱',
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                        render: (_, record) =>
                          record.description || record.name || '減項項目'
                      },
                      {
                        title: '日期',
                        key: 'date',
                        width: 100,
                        render: (_, record) => (
                          <span>
                            {record.date
                              ? dayjs(record.date).format('MM-DD')
                              : ''}
                          </span>
                        )
                      },
                      {
                        title: '金額',
                        dataIndex: 'amount',
                        key: 'amount',
                        align: 'right',
                        width: 100,
                        render: (_, record) => (
                          <ItemAmount negative>
                            {formatCurrency(
                              Number(record.amount ?? record.value ?? 0)
                            )}
                          </ItemAmount>
                        )
                      },
                      {
                        title: '操作',
                        key: 'action',
                        width: 80,
                        align: 'center',
                        render: (_, record) => (
                          <Button
                            type='link'
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewItemDetail(record)
                            }}
                          >
                            詳情
                          </Button>
                        )
                      }
                    ]}
                    pagination={false}
                    size='small'
                    scroll={{ x: 'max-content', y: 400 }}
                    onRow={(record) => ({
                      onClick: () => handleViewItemDetail(record)
                    })}
                  />
                  <TotalSection>
                    <TotalLabel>
                      {deductionsCategoryFilter ? '篩選小計：' : '減項總計：'}
                    </TotalLabel>
                    <TotalValue negative>
                      {formatCurrency(
                        deductionsCategoryFilter
                          ? filteredDeductionsTotal
                          : deductionsTotal
                      )}
                    </TotalValue>
                  </TotalSection>
                </>
              ) : (
                <Empty
                  description='本月尚無減項紀錄'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: '40px 0' }}
                />
              )}
            </Spin>
          </SectionCard>
        </DetailContent>

        {/* 項目詳情 Modal */}
        <StyledModal
          title={
            <div className='modal-title-content'>
              {(() => {
                const category =
                  selectedItem?.category || selectedItem?.type || '項目詳情'
                const isDeduction =
                  category === '減項項目' || selectedItem?.type === 'deduction'

                // 根據類別決定標籤顏色
                let badgeColor = isDeduction ? 'red' : 'success'
                if (
                  category.includes('技術服務') ||
                  category.includes('技术服务') ||
                  category === 'serviceCommission' ||
                  category === '技術服務抽成'
                ) {
                  badgeColor = 'blue'
                } else if (
                  category.includes('儲值') ||
                  category.includes('储值') ||
                  category === 'valueAddedCommission' ||
                  category === '儲值抽成'
                ) {
                  badgeColor = 'green'
                } else if (
                  category.includes('產品銷售') ||
                  category.includes('产品销售') ||
                  category === 'productSalesCommission' ||
                  category === '產品銷售抽成'
                ) {
                  badgeColor = 'orange'
                } else if (
                  category.includes('堂票') ||
                  category === 'classTicketCommission' ||
                  category === '堂票銷售抽成'
                ) {
                  badgeColor = 'purple'
                }

                return (
                  <>
                    <Tag color={badgeColor}>{category}</Tag>
                    <span style={{ marginLeft: spacing.sm }}>
                      {selectedItem?.description ||
                        selectedItem?.name ||
                        '項目詳情'}
                    </span>
                  </>
                )
              })()}
            </div>
          }
          open={itemDetailModalVisible}
          onCancel={handleCloseItemDetailModal}
          footer={[
            <Button key='close' onClick={handleCloseItemDetailModal}>
              關閉
            </Button>
          ]}
          width={800}
          style={{
            top: 20
          }}
        >
          {selectedItem && (
            <DetailModalContent>
              {selectedItem.details?.teamBonusSaleOnly && (
                <TeamBonusNotice>
                  <b>這是銷售紀錄，只列入業績，不另外抽成。</b>
                  <br />
                  此商品屬於團積獎金商品：全店每賣出 1 件，獎金{' '}
                  {formatCurrency(
                    Number(selectedItem.details.teamBonusAmountPerUnit || 0)
                  )}{' '}
                  由 {selectedItem.details.teamBonusMemberCount || 0}{' '}
                  人平分，獎金會另外列在「團積獎金」項目。
                </TeamBonusNotice>
              )}
              {selectedItem.details?.isTeamBonus && (
                <TeamBonusNotice>
                  <b>團積獎金</b>：全店本月賣出{' '}
                  {selectedItem.details.teamBonusQty} 件 ×{' '}
                  {formatCurrency(
                    Number(selectedItem.details.teamBonusAmountPerUnit || 0)
                  )}{' '}
                  ={' '}
                  {formatCurrency(Number(selectedItem.details.teamBonusPool || 0))}
                  ，由 {selectedItem.details.teamBonusMemberCount} 人平分，每人{' '}
                  {formatCurrency(Number(selectedItem.amount || 0))}。
                  <br />
                  各筆銷售紀錄只列入賣出人的業績，不另外抽成。
                </TeamBonusNotice>
              )}
              <div className='detail-section'>
                <div className='detail-section-title'>基本資訊</div>
                <ItemDetailsGrid>
                  <DetailRow>
                    <DetailLabel>
                      {selectedItem.details?.teamBonusSaleOnly
                        ? '抽成：'
                        : selectedItem.details?.isTeamBonus
                        ? '每人獎金：'
                        : '金額：'}
                    </DetailLabel>
                    <ItemAmount
                      negative={
                        selectedItem?.category === '減項項目' ||
                        selectedItem?.type === 'deduction'
                      }
                    >
                      {formatCurrency(
                        Number(selectedItem.amount ?? selectedItem.value ?? 0)
                      )}
                    </ItemAmount>
                  </DetailRow>
                  {(selectedItem.date || selectedItem.time) && (
                    <DetailRow>
                      <DetailLabel>日期時間：</DetailLabel>
                      <span>
                        {selectedItem.date
                          ? dayjs(selectedItem.date).format('YYYY-MM-DD')
                          : ''}
                        {selectedItem.date && selectedItem.time ? ' ' : ''}
                        {selectedItem.time || ''}
                      </span>
                    </DetailRow>
                  )}
                </ItemDetailsGrid>
              </div>

              {selectedItem.details && (
                <div className='detail-section'>
                  <div className='detail-section-title'>詳細資訊</div>
                  <ItemDetailsGrid>
                    {/* 技術服務抽成專用欄位 */}
                    {selectedItem.details.orderId && (
                      <DetailRow>
                        <DetailLabel>訂單ID：</DetailLabel>
                        <span
                          style={{
                            fontSize: '11px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {selectedItem.details.orderId}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.bookingId && (
                      <DetailRow>
                        <DetailLabel>預約ID：</DetailLabel>
                        <span
                          style={{
                            fontSize: '11px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {selectedItem.details.bookingId}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.customerName && (
                      <DetailRow>
                        <DetailLabel>客戶：</DetailLabel>
                        <span>{selectedItem.details.customerName}</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.customerPhone && (
                      <DetailRow>
                        <DetailLabel>電話：</DetailLabel>
                        <span>{selectedItem.details.customerPhone}</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.serviceName && (
                      <DetailRow>
                        <DetailLabel>服務：</DetailLabel>
                        <span>{selectedItem.details.serviceName}</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.serviceAmount !== undefined && (
                      <DetailRow>
                        <DetailLabel>金額：</DetailLabel>
                        <span>
                          {formatCurrency(selectedItem.details.serviceAmount)}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.originalPrice !== undefined && (
                      <DetailRow>
                        <DetailLabel>原價：</DetailLabel>
                        <span>
                          {formatCurrency(selectedItem.details.originalPrice)}
                        </span>
                      </DetailRow>
                    )}
                    {(selectedItem.details.commissionType ||
                      selectedItem.details.commissionTypeDisplay) && (
                      <DetailRow>
                        <DetailLabel>抽成類型：</DetailLabel>
                        <div>
                          {(() => {
                            const details = selectedItem.details || {}

                            // 優先從 serviceItems 數組中獲取數據
                            const serviceItems = details.serviceItems || []
                            const firstServiceItem = serviceItems[0] || {}

                            const commissionType = details.commissionType
                            const isSamePerson =
                              firstServiceItem.isSamePerson ??
                              details.isSamePerson
                            const designerCommission =
                              firstServiceItem.designerCommission ??
                              details.designerCommission
                            const assistantCommission =
                              firstServiceItem.assistantCommission ??
                              details.assistantCommission
                            const commissionAmount =
                              firstServiceItem.commissionAmount ??
                              details.commissionAmount ??
                              selectedItem.amount ??
                              0
                            const upgradeMultiplier =
                              firstServiceItem.upgradeMultiplier ??
                              details.upgradeMultiplier

                            // 如果是設計師和助理抽成類型
                            if (
                              commissionType === 'designer_and_assistant' &&
                              (designerCommission !== undefined ||
                                assistantCommission !== undefined)
                            ) {
                              const items = []

                              // 設計師抽成
                              if (designerCommission !== undefined) {
                                let percentage = ''
                                if (commissionAmount > 0 && upgradeMultiplier) {
                                  const designerRate = Math.round(
                                    (designerCommission / commissionAmount) *
                                      100
                                  )
                                  percentage = `（${designerRate}%）`
                                } else if (upgradeMultiplier) {
                                  const baseRate = Math.round(
                                    100 / (upgradeMultiplier + 1)
                                  )
                                  const designerRate =
                                    baseRate * upgradeMultiplier
                                  percentage = `（${designerRate}%）`
                                }
                                items.push(
                                  <div
                                    key='designer'
                                    style={{ marginBottom: 4 }}
                                  >
                                    設計師抽成：
                                    {formatCurrency(designerCommission)}
                                    {percentage}
                                  </div>
                                )
                              }

                              // 助理抽成
                              if (assistantCommission !== undefined) {
                                let percentage = ''
                                if (commissionAmount > 0 && upgradeMultiplier) {
                                  const assistantRate = Math.round(
                                    (assistantCommission / commissionAmount) *
                                      100
                                  )
                                  percentage = `（${assistantRate}%）`
                                } else if (upgradeMultiplier) {
                                  const assistantRate = Math.round(
                                    100 / (upgradeMultiplier + 1)
                                  )
                                  percentage = `（${assistantRate}%）`
                                }
                                items.push(
                                  <div
                                    key='assistant'
                                    style={{ marginBottom: 4 }}
                                  >
                                    助理抽成：
                                    {formatCurrency(assistantCommission)}
                                    {percentage}
                                  </div>
                                )
                              }

                              // 總抽成
                              if (commissionAmount > 0) {
                                items.push(
                                  <div
                                    key='total'
                                    style={{
                                      marginTop: 4,
                                      paddingTop: 4,
                                      borderTop: '1px solid #e5e5e7',
                                      fontWeight: 600
                                    }}
                                  >
                                    總抽成：{formatCurrency(commissionAmount)}
                                  </div>
                                )
                              }

                              // 顯示計算公式
                              const commissionRateInfo =
                                firstServiceItem.commissionRateInfo ??
                                details.commissionRateInfo
                              if (commissionRateInfo?.calculation) {
                                items.push(
                                  <div
                                    key='calculation'
                                    style={{
                                      marginTop: 6,
                                      paddingTop: 6,
                                      borderTop: '1px solid #f0f0f0',
                                      fontSize: '11px',
                                      color: '#8c8c8c',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    {commissionRateInfo.calculation}
                                  </div>
                                )
                              }

                              return items
                            }

                            // 其他情況，顯示原有的抽成類型
                            return (
                              details.commissionTypeDisplay ||
                              (commissionType === 'designer'
                                ? '設計師'
                                : commissionType === 'assistant'
                                  ? '助理'
                                  : commissionType === 'designer_first_dose'
                                    ? '設計師 - 第一劑'
                                    : commissionType === 'designer_second_dose'
                                      ? '設計師 - 第二劑'
                                      : commissionType)
                            )
                          })()}
                        </div>
                      </DetailRow>
                    )}
                    {/* 顯示計算公式 */}
                    {(() => {
                      const details = selectedItem.details || {}
                      const serviceItems = details.serviceItems || []
                      const firstServiceItem = serviceItems[0] || {}
                      const commissionRateInfo =
                        firstServiceItem.commissionRateInfo ??
                        details.commissionRateInfo
                      if (commissionRateInfo?.calculation) {
                        return (
                          <DetailRow>
                            <DetailLabel>計算：</DetailLabel>
                            <span
                              style={{
                                fontSize: '11px',
                                color: '#8c8c8c',
                                fontStyle: 'italic'
                              }}
                            >
                              {commissionRateInfo.calculation}
                            </span>
                          </DetailRow>
                        )
                      }
                      return null
                    })()}
                    {selectedItem.details.assistCommission !== undefined &&
                      selectedItem.details.assistCommission > 0 && (
                        <DetailRow>
                          <DetailLabel>協助抽成：</DetailLabel>
                          <span>
                            {formatCurrency(
                              selectedItem.details.assistCommission
                            )}
                          </span>
                        </DetailRow>
                      )}
                    {selectedItem.details.developCommission !== undefined &&
                      selectedItem.details.developCommission > 0 && (
                        <DetailRow>
                          <DetailLabel>開發抽成：</DetailLabel>
                          <span>
                            {formatCurrency(
                              selectedItem.details.developCommission
                            )}
                          </span>
                        </DetailRow>
                      )}
                    {selectedItem.details.commissionAmount !== undefined && (
                      <DetailRow>
                        <DetailLabel>抽成金額：</DetailLabel>
                        <span
                          style={{ fontWeight: 600, color: colors.success }}
                        >
                          {formatCurrency(
                            selectedItem.details.commissionAmount
                          )}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.isRecalculated !== undefined && (
                      <DetailRow>
                        <DetailLabel>重新計算：</DetailLabel>
                        <span>
                          {selectedItem.details.isRecalculated ? '是' : '否'}
                        </span>
                      </DetailRow>
                    )}

                    {/* 儲值抽成專用欄位 */}
                    {selectedItem.details.purchaseId && (
                      <DetailRow>
                        <DetailLabel>儲值ID：</DetailLabel>
                        <span
                          style={{
                            fontSize: '11px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {selectedItem.details.purchaseId}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.valueAddedAmount !== undefined && (
                      <DetailRow>
                        <DetailLabel>儲值金額：</DetailLabel>
                        <span>
                          {formatCurrency(
                            selectedItem.details.valueAddedAmount
                          )}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.totalPayment !== undefined && (
                      <DetailRow>
                        <DetailLabel>總付款：</DetailLabel>
                        <span>
                          {formatCurrency(selectedItem.details.totalPayment)}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.discount !== undefined && (
                      <DetailRow>
                        <DetailLabel>折扣：</DetailLabel>
                        <span>{selectedItem.details.discount}%</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.commissionRate !== undefined &&
                      !selectedItem.details.teamBonusSaleOnly &&
                      !selectedItem.details.isTeamBonus && (
                      <DetailRow>
                        <DetailLabel>抽成比例：</DetailLabel>
                        <span>{selectedItem.details.commissionRate}%</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.cumulativeSales !== undefined && (
                      <DetailRow>
                        <DetailLabel>累積業績：</DetailLabel>
                        <span>
                          {formatCurrency(selectedItem.details.cumulativeSales)}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.threshold !== undefined && (
                      <DetailRow>
                        <DetailLabel>門檻：</DetailLabel>
                        <span>
                          {formatCurrency(selectedItem.details.threshold)}
                        </span>
                      </DetailRow>
                    )}

                    {/* 產品銷售抽成專用欄位 */}
                    {selectedItem.details.productId && (
                      <DetailRow>
                        <DetailLabel>產品ID：</DetailLabel>
                        <span
                          style={{
                            fontSize: '11px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {selectedItem.details.productId}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.productName && (
                      <DetailRow>
                        <DetailLabel>產品名稱：</DetailLabel>
                        <span>{selectedItem.details.productName}</span>
                      </DetailRow>
                    )}
                    {(selectedItem.details.productAmount !== undefined ||
                      selectedItem.details.productSalesAmount !==
                        undefined) &&
                      !selectedItem.details.isTeamBonus && (
                      <DetailRow>
                        <DetailLabel>
                          {selectedItem.details.teamBonusSaleOnly
                            ? '列入業績：'
                            : '價格：'}
                        </DetailLabel>
                        <span>
                          {formatCurrency(
                            Number(
                              selectedItem.details.productAmount ??
                                selectedItem.details.productSalesAmount
                            )
                          )}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.quantity !== undefined &&
                      !selectedItem.details.isTeamBonus && (
                      <DetailRow>
                        <DetailLabel>數量：</DetailLabel>
                        <span>{selectedItem.details.quantity}</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.unitPrice !== undefined &&
                      !selectedItem.details.isTeamBonus && (
                      <DetailRow>
                        <DetailLabel>單價：</DetailLabel>
                        <span>
                          {formatCurrency(
                            Number(selectedItem.details.unitPrice)
                          )}
                        </span>
                      </DetailRow>
                    )}
                    {(selectedItem.details.productCommissionRate !==
                      undefined ||
                      selectedItem.details.commissionRate !== undefined) &&
                      selectedItem.type !== 'classTicketCommission' &&
                      !selectedItem.details.teamBonusSaleOnly &&
                      !selectedItem.details.isTeamBonus && (
                        <DetailRow>
                          <DetailLabel>產品抽成比例：</DetailLabel>
                          <span>
                            {selectedItem.details.productCommissionRate ??
                              selectedItem.details.commissionRate}
                            %
                          </span>
                        </DetailRow>
                      )}

                    {/* 堂票銷售抽成專用欄位 */}
                    {selectedItem.details.ticketCode && (
                      <DetailRow>
                        <DetailLabel>堂票編號：</DetailLabel>
                        <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                          {selectedItem.details.ticketCode}
                        </span>
                      </DetailRow>
                    )}
                    {selectedItem.details.templateName && (
                      <DetailRow>
                        <DetailLabel>堂票名稱：</DetailLabel>
                        <span>{selectedItem.details.templateName}</span>
                      </DetailRow>
                    )}
                    {selectedItem.details.price !== undefined &&
                      selectedItem.type === 'classTicketCommission' && (
                        <DetailRow>
                          <DetailLabel>售價：</DetailLabel>
                          <span>
                            {formatCurrency(selectedItem.details.price)}
                          </span>
                        </DetailRow>
                      )}
                    {selectedItem.details.paymentMethod &&
                      selectedItem.type === 'classTicketCommission' && (() => {
                        const pmMap = {
                          cash: '💵 現金',
                          card: '💳 刷卡',
                          mobile_payment: '📱 行動支付',
                          transfer: '🏦 轉帳'
                        }
                        return (
                          <DetailRow>
                            <DetailLabel>支付方式：</DetailLabel>
                            <span>
                              {pmMap[selectedItem.details.paymentMethod] ||
                                selectedItem.details.paymentMethod}
                            </span>
                          </DetailRow>
                        )
                      })()}
                    {selectedItem.details.tierRate !== undefined &&
                      selectedItem.type === 'classTicketCommission' && (
                        <DetailRow>
                          <DetailLabel>抽成比例：</DetailLabel>
                          <span>{selectedItem.details.tierRate}%</span>
                        </DetailRow>
                      )}
                    {selectedItem.details.fee !== undefined &&
                      selectedItem.details.fee > 0 &&
                      selectedItem.type === 'classTicketCommission' && (
                        <DetailRow>
                          <DetailLabel>手續費：</DetailLabel>
                          <span style={{ color: colors.error }}>
                            − {formatCurrency(selectedItem.details.fee)}
                          </span>
                        </DetailRow>
                      )}
                  </ItemDetailsGrid>

                  {/* 服務抽成手續費計算明細（profit_rate_minus_fee 規則） */}
                  {selectedItem.type === 'serviceCommission' &&
                    selectedItem.details?.commissionRule === 'profit_rate_minus_fee' &&
                    selectedItem.details?.serviceFee !== undefined &&
                    selectedItem.details.serviceFee > 0 && (() => {
                      const fee = Number(selectedItem.details.serviceFee || 0)
                      const finalCommission = Number(selectedItem.amount || 0)
                      const baseCommission = finalCommission + fee
                      return (
                        <div
                          style={{
                            marginTop: 16,
                            padding: '16px 18px',
                            borderRadius: 12,
                            border: '1px dashed #b3d4f5',
                            background: '#f0f7ff'
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1677ff', marginBottom: 12, letterSpacing: 0.5 }}>
                            計算明細（扣手續費）
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>抽成（扣費前）</span>
                              <span style={{ color: '#1c1c1e' }}>{formatCurrency(baseCommission)}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>分攤手續費</span>
                              <span style={{ color: '#e74c3c' }}>− {formatCurrency(fee)}</span>
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: 14,
                              padding: '12px 14px',
                              borderRadius: 10,
                              background: '#fff',
                              border: '1px solid #b3d4f5',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'baseline',
                              gap: 8,
                              fontSize: 14,
                              lineHeight: 1.6
                            }}
                          >
                            <span style={{ color: '#576079', fontWeight: 600 }}>最終抽成：</span>
                            <span>
                              {formatCurrency(baseCommission)}{' '}
                              <span style={{ color: '#8e8e93' }}>（抽成）</span>{' '}
                              − {formatCurrency(fee)}{' '}
                              <span style={{ color: '#8e8e93' }}>（手續費）</span>{' '}
                              ={' '}
                              <b style={{ color: '#1677ff', fontSize: 16 }}>
                                {formatCurrency(finalCommission)}
                              </b>
                            </span>
                          </div>
                        </div>
                      )
                    })()}

                  {/*
                    新規則「按利潤抽成扣手續費」的計算明細
                    僅在 commissionRule === 'profit_rate_minus_fee' 且
                    當前使用者有 salary:view_cost_detail 權限時顯示。
                    （成本/手續費為商業敏感資訊，預設僅店長／會計／老闆可見；
                     店家可於「角色管理 → 自定義許可權」開放給特定員工）
                    用垂直 flex 排版（不用 grid），避免文字被擠壓換行。
                  */}
                  {selectedItem.type === 'productSalesCommission' &&
                    selectedItem.details.commissionRule ===
                    'profit_rate_minus_fee' &&
                    (businessRole === 'owner' ||
                      permissionList.includes('*') ||
                      permissionList.includes('salary:view_cost_detail')) &&
                    (() => {
                      const profit = Number(
                        selectedItem.details.lineProfit || 0
                      )
                      const rate = Number(
                        selectedItem.details.commissionRate || 0
                      )
                      const baseCommission = Math.round((profit * rate) / 100)
                      const fee = Number(selectedItem.details.lineFee || 0)
                      const finalCommission = Math.max(
                        0,
                        baseCommission - fee
                      )

                      return (
                        <div
                          style={{
                            marginTop: 16,
                            padding: '16px 18px',
                            borderRadius: 12,
                            border: '1px dashed #f0ccd4',
                            background: '#fffafb'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#d5728a',
                              marginBottom: 12,
                              letterSpacing: 0.5
                            }}
                          >
                            計算明細
                          </div>

                          {/* 垂直清單，每行一個資訊，標籤右對齊到固定寬度 */}
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              fontSize: 13,
                              lineHeight: 1.5
                            }}
                          >
                            {selectedItem.details.unitCost !== undefined && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span
                                  style={{
                                    minWidth: 96,
                                    color: '#8e8e93',
                                    flexShrink: 0
                                  }}
                                >
                                  單品成本
                                </span>
                                <span style={{ color: '#1c1c1e' }}>
                                  {formatCurrency(
                                    Number(selectedItem.details.unitCost)
                                  )}
                                </span>
                              </div>
                            )}

                            {selectedItem.details.productAmount !==
                              undefined && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span
                                  style={{
                                    minWidth: 96,
                                    color: '#8e8e93',
                                    flexShrink: 0
                                  }}
                                >
                                  商品實收
                                </span>
                                <span style={{ color: '#1c1c1e' }}>
                                  {formatCurrency(
                                    Number(
                                      selectedItem.details.productAmount
                                    )
                                  )}
                                  <span
                                    style={{
                                      color: '#8e8e93',
                                      marginLeft: 6,
                                      fontSize: 12
                                    }}
                                  >
                                    （已扣折扣/優惠券分攤）
                                  </span>
                                </span>
                              </div>
                            )}

                            {selectedItem.details.lineProfit !== undefined && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span
                                  style={{
                                    minWidth: 96,
                                    color: '#8e8e93',
                                    flexShrink: 0
                                  }}
                                >
                                  本筆利潤
                                </span>
                                <span style={{ color: '#27ae60' }}>
                                  {formatCurrency(profit)}
                                  <span
                                    style={{
                                      color: '#8e8e93',
                                      marginLeft: 6,
                                      fontSize: 12
                                    }}
                                  >
                                    （實收 − 成本 ×{' '}
                                    {selectedItem.details.quantity || 1} 件）
                                  </span>
                                </span>
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: 8 }}>
                              <span
                                style={{
                                  minWidth: 96,
                                  color: '#8e8e93',
                                  flexShrink: 0
                                }}
                              >
                                抽成（扣費前）
                              </span>
                              <span style={{ color: '#1c1c1e' }}>
                                {formatCurrency(baseCommission)}
                              </span>
                            </div>

                            {selectedItem.details.lineFee !== undefined && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span
                                  style={{
                                    minWidth: 96,
                                    color: '#8e8e93',
                                    flexShrink: 0
                                  }}
                                >
                                  分攤手續費
                                </span>
                                <span style={{ color: '#e74c3c' }}>
                                  − {formatCurrency(fee)}
                                  <span
                                    style={{
                                      color: '#8e8e93',
                                      marginLeft: 6,
                                      fontSize: 12
                                    }}
                                  >
                                    （依商品金額占訂單實收比例分攤）
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>

                          {/* 最終結果條 — 口語化呈現 */}
                          <div
                            style={{
                              marginTop: 14,
                              padding: '12px 14px',
                              borderRadius: 10,
                              background: '#fff',
                              border: '1px solid #f0ccd4',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'baseline',
                              gap: 8,
                              fontSize: 14,
                              lineHeight: 1.6
                            }}
                          >
                            <span style={{ color: '#576079', fontWeight: 600 }}>
                              最終抽成：
                            </span>
                            <span>
                              {formatCurrency(baseCommission)}{' '}
                              <span style={{ color: '#8e8e93' }}>
                                （抽成）
                              </span>{' '}
                              −{' '}
                              {formatCurrency(fee)}{' '}
                              <span style={{ color: '#8e8e93' }}>
                                （手續費）
                              </span>{' '}
                              ={' '}
                              <b
                                style={{
                                  color: '#d5728a',
                                  fontSize: 16
                                }}
                              >
                                {formatCurrency(finalCommission)}
                              </b>
                            </span>
                          </div>
                        </div>
                      )
                    })()}

                  {/* 堂票抽成計算明細（扣手續費規則） */}
                  {selectedItem.type === 'classTicketCommission' &&
                    selectedItem.details.fee !== undefined &&
                    selectedItem.details.fee > 0 && (() => {
                      const price = Number(selectedItem.details.price || 0)
                      const rate = Number(selectedItem.details.tierRate || 0)
                      const baseCommission = Math.round((price * rate) / 100)
                      const fee = Number(selectedItem.details.fee || 0)
                      const finalCommission = Math.max(0, baseCommission - fee)
                      return (
                        <div
                          style={{
                            marginTop: 16,
                            padding: '16px 18px',
                            borderRadius: 12,
                            border: '1px dashed #c6b0e0',
                            background: '#faf5ff'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#722ed1',
                              marginBottom: 12,
                              letterSpacing: 0.5
                            }}
                          >
                            計算明細（扣手續費）
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              fontSize: 13,
                              lineHeight: 1.5
                            }}
                          >
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>
                                堂票售價
                              </span>
                              <span style={{ color: '#1c1c1e' }}>
                                {formatCurrency(price)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>
                                抽成比例
                              </span>
                              <span style={{ color: '#1c1c1e' }}>
                                {rate}%
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>
                                抽成（扣費前）
                              </span>
                              <span style={{ color: '#1c1c1e' }}>
                                {formatCurrency(baseCommission)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ minWidth: 96, color: '#8e8e93', flexShrink: 0 }}>
                                金流手續費
                              </span>
                              <span style={{ color: '#e74c3c' }}>
                                − {formatCurrency(fee)}
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: 14,
                              padding: '12px 14px',
                              borderRadius: 10,
                              background: '#fff',
                              border: '1px solid #c6b0e0',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'baseline',
                              gap: 8,
                              fontSize: 14,
                              lineHeight: 1.6
                            }}
                          >
                            <span style={{ color: '#576079', fontWeight: 600 }}>
                              最終抽成：
                            </span>
                            <span>
                              {formatCurrency(baseCommission)}{' '}
                              <span style={{ color: '#8e8e93' }}>（抽成）</span>{' '}
                              − {formatCurrency(fee)}{' '}
                              <span style={{ color: '#8e8e93' }}>（手續費）</span>{' '}
                              ={' '}
                              <b style={{ color: '#722ed1', fontSize: 16 }}>
                                {formatCurrency(finalCommission)}
                              </b>
                            </span>
                          </div>
                        </div>
                      )
                    })()}

                  {/* 服務項目明細（技術服務抽成） */}
                  {selectedItem.details.serviceItems &&
                    Array.isArray(selectedItem.details.serviceItems) &&
                    selectedItem.details.serviceItems.length > 0 && (
                      <div
                        style={{
                          marginTop: spacing.md,
                          paddingTop: spacing.md,
                          borderTop: `1px solid ${colors.border}`
                        }}
                      >
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: colors.text.primary,
                            marginBottom: spacing.sm
                          }}
                        >
                          服務項目明細：
                        </div>
                        {selectedItem.details.serviceItems.map(
                          (serviceItem, sIdx) => (
                            <div
                              key={sIdx}
                              style={{
                                background: colors.background.tertiary,
                                borderRadius: 6,
                                padding: spacing.sm,
                                marginBottom:
                                  sIdx <
                                  selectedItem.details.serviceItems.length - 1
                                    ? spacing.sm
                                    : 0,
                                border: `1px solid ${colors.border}`
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 500,
                                  color: colors.text.primary,
                                  marginBottom: spacing.xs,
                                  fontSize: 14
                                }}
                              >
                                {serviceItem.serviceName}
                              </div>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(2, 1fr)',
                                  gap: `${spacing.xs} ${spacing.sm}`,
                                  fontSize: 12,
                                  color: colors.text.secondary
                                }}
                              >
                                <span>
                                  實收：
                                  {formatCurrency(serviceItem.serviceAmount)}
                                </span>
                                <span>
                                  原價：
                                  {formatCurrency(serviceItem.originalPrice)}
                                </span>
                                {serviceItem.assistCommission > 0 && (
                                  <span>
                                    協助：
                                    {formatCurrency(
                                      serviceItem.assistCommission
                                    )}
                                  </span>
                                )}
                                {serviceItem.developCommission > 0 && (
                                  <span>
                                    開發：
                                    {formatCurrency(
                                      serviceItem.developCommission
                                    )}
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: colors.success
                                  }}
                                >
                                  抽成：
                                  {formatCurrency(serviceItem.commissionAmount)}
                                </span>
                              </div>
                              {serviceItem.commissionRateInfo?.calculation && (
                                <div
                                  style={{
                                    marginTop: spacing.xs,
                                    paddingTop: spacing.xs,
                                    borderTop: `1px solid ${colors.border}`,
                                    fontSize: '11px',
                                    color: '#8c8c8c',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {serviceItem.commissionRateInfo.calculation}
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    )}
                </div>
              )}
            </DetailModalContent>
          )}
        </StyledModal>
      </DetailPageWrapper>
    )
  }

  // 權限檢查
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={14}>
              <Title
                level={3}
                style={{
                  marginBottom: spacing.xs,
                  color: colors.text.primary,
                  fontSize: 28,
                  fontWeight: 300
                }}
              >
                {isOwner ? '每月員工薪資' : '我的每月薪資'}
              </Title>
              <Paragraph
                style={{
                  color: colors.text.secondary,
                  marginBottom: 0,
                  fontSize: 14
                }}
              >
                {isOwner
                  ? '依月份查看各員工加項、減項與實際提撥薪資，快速掌握支出狀況。'
                  : '依月份查看個人加項、減項與實際提撥薪資，掌握本月薪資結構。'}
              </Paragraph>
            </Col>
            <Col xs={24} md={10}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  background: colors.background.secondary,
                  border: `1px solid ${colors.border}`,
                  height: '100%'
                }}
              >
                <Statistic
                  title='本月總提撥薪資'
                  value={formatCurrency(totalNetSalary)}
                  prefix={
                    <DollarOutlined style={{ color: colors.secondary }} />
                  }
                  valueStyle={{
                    color:
                      totalNetSalary < 0 ? colors.error : colors.text.primary,
                    fontSize: 24,
                    fontWeight: 500
                  }}
                />
              </Card>
            </Col>
          </Row>
        </StyledCard>

        <Card
          style={{
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
          }}
        >
          <FilterBar>
            <DatePicker
              picker='month'
              allowClear={false}
              value={selectedMonth}
              onChange={handleMonthChange}
              format='YYYY年MM月'
              suffixIcon={<CalendarOutlined />}
              size='large'
            />
            <Button
              icon={<ReloadOutlined />}
              size='large'
              onClick={handleRefresh}
            >
              重新整理
            </Button>
            <Button
              icon={<DownloadOutlined />}
              size='large'
              onClick={handleExportCsv}
              loading={exportCsvLoading}
            >
              匯出 CSV
            </Button>
            {isOwner && (
              <Tooltip
                title='如果修改過抽成規則，可以點擊此按鈕重新計算當月薪資。重新計算會使用最新的抽成規則更新所有員工的薪資數據。'
                placement='top'
              >
                <Button
                  icon={<CalculatorOutlined />}
                  size='large'
                  onClick={handleRecalculate}
                  loading={loading}
                  type='primary'
                >
                  重新計算
                </Button>
              </Tooltip>
            )}
            {lastUpdated && (
              <Text type='secondary'>
                最近更新：{dayjs(lastUpdated).format('YYYY/MM/DD HH:mm')}
              </Text>
            )}
          </FilterBar>
        </Card>

        <TableCard
          title='薪資明細'
          extra={
            <Row gutter={16}>
              <Col>
                <Statistic
                  title='加項總計'
                  value={formatCurrency(totalAdditions)}
                  valueStyle={{
                    color: colors.text.primary,
                    fontSize: 18,
                    fontWeight: 500
                  }}
                />
              </Col>
              <Col>
                <Statistic
                  title='減項總計'
                  value={formatCurrency(totalDeductions)}
                  valueStyle={{
                    color: colors.error,
                    fontSize: 18,
                    fontWeight: 500
                  }}
                />
              </Col>
            </Row>
          }
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 0'
              }}
            >
              <Spin size='large' />
            </div>
          ) : visibleRecords.length === 0 ? (
            <Empty description='本月尚無薪資紀錄' />
          ) : isMobile ? (
            <>
              <MobileCardList>
                {visibleRecords
                  .slice(
                    (mobilePage - 1) * mobilePageSize,
                    mobilePage * mobilePageSize
                  )
                  .map((record) => (
                    <MobileSalaryCard
                      key={record.key}
                      onClick={() => handleViewDetails(record)}
                    >
                      <MobileCardLeft>
                        <MobileCardEmployeeName>
                          {record.employeeName}
                        </MobileCardEmployeeName>
                        <MobileCardSalary>
                          {formatCurrency(record.netSalary)}
                        </MobileCardSalary>
                      </MobileCardLeft>
                      <MobileCardIcon>
                        <EyeOutlined />
                      </MobileCardIcon>
                    </MobileSalaryCard>
                  ))}
              </MobileCardList>
              {visibleRecords.length > mobilePageSize && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: spacing.sm,
                    paddingTop: spacing.sm,
                    borderTop: `1px solid ${colors.border}`
                  }}
                >
                  <Pagination
                    current={mobilePage}
                    pageSize={mobilePageSize}
                    total={visibleRecords.length}
                    onChange={(page) => setMobilePage(page)}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    size='small'
                    simple
                  />
                </div>
              )}
            </>
          ) : (
            <Table
              rowKey='key'
              columns={columns}
              dataSource={visibleRecords}
              pagination={{ pageSize: 12 }}
              scroll={{ x: 1600 }}
            />
          )}
        </TableCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default MonthlySalarySummaryPage
